<template>
  <Layout>
    <div class="event-dashboard">
      <!-- Header -->
      <div class="sa-header" data-tour="dashboard-header">
        <div>
          <h4 class="sa-title">Dashboard do Evento</h4>
          <p class="sa-subtitle" v-if="lastUpdate">Atualizado h&aacute; {{ timeAgo }}</p>
        </div>
        <div class="sa-header-right">
          <div class="sa-tabs">
            <button
              v-for="range in rangeOptions"
              :key="range.value"
              type="button"
              class="sa-tab"
              :class="{ active: selectedRange === range.value }"
              @click="selectRange(range.value)"
            >{{ range.label }}</button>
            <span class="sa-tab-sep"></span>
            <div class="sa-date-picker-wrap">
              <button
                type="button"
                class="sa-tab"
                :class="{ active: selectedRange === 'custom' }"
                @click="showDatePicker = !showDatePicker"
                title="Período personalizado"
              ><i class="mdi mdi-calendar-range"></i></button>
              <div v-if="showDatePicker" class="sa-date-dropdown">
                <label class="sa-date-dropdown-label">De</label>
                <input type="date" v-model="customDates.start" class="sa-date-dropdown-input" :max="customDates.end" />
                <label class="sa-date-dropdown-label">Até</label>
                <input type="date" v-model="customDates.end" class="sa-date-dropdown-input" :min="customDates.start" />
                <div class="sa-date-dropdown-actions">
                  <button type="button" class="sa-date-dropdown-btn cancel" @click="clearFilters">Limpar</button>
                  <button type="button" class="sa-date-dropdown-btn apply" @click="applyCustomDates">Aplicar</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading && !dashboard" class="text-center py-5">
        <b-spinner variant="primary"></b-spinner>
        <p class="mt-2 text-muted small">Carregando dados...</p>
      </div>

      <!-- Dashboard Content -->
      <template v-else-if="dashboard">

        <!-- ROW 0 - Event Info Card -->
        <div class="event-info-card" v-if="dashboard.event_info && dashboard.event_info.name">
          <div class="event-info-top">
            <h2 class="event-info-name">{{ dashboard.event_info.name }}</h2>
            <div class="event-info-actions">
              <a
                v-if="dashboard.event_info.lp_url"
                :href="dashboard.event_info.lp_url"
                target="_blank"
                class="btn-lp"
              >
                <i class="bx bx-link-external"></i> Ver Landing Page
              </a>
              <button
                v-if="dashboard.event_info.lp_url"
                class="btn-lp btn-lp--outline"
                @click="copyLpUrl"
              >
                <i class="bx" :class="lpCopied ? 'bx-check' : 'bx-copy'"></i>
                {{ lpCopied ? 'Copiado!' : 'Copiar Link' }}
              </button>
            </div>
          </div>
          <div class="event-info-meta">
            <span class="event-info-meta-item" :class="{ 'event-info-meta-item--muted': !eventDateRange }">
              <i class="bx bx-calendar"></i> {{ eventDateRange || 'Data a definir' }}
            </span>
            <span v-if="dashboard.event_info.local_name" class="event-info-meta-item">
              <i class="bx bx-map"></i> {{ dashboard.event_info.local_name }}
            </span>
            <span v-if="dashboard.event_info.organizer" class="event-info-meta-item">
              <i class="bx bx-user"></i> {{ dashboard.event_info.organizer }}
            </span>
          </div>
          <div class="event-info-badges">
            <span class="event-badge" :class="eventModeBadgeClass">
              <i class="bx" :class="eventModeIcon"></i> {{ eventModeLabel }}
            </span>
            <span class="event-badge" :class="registrationStatusBadgeClass">
              {{ registrationStatusLabel }}
            </span>
            <span class="event-badge event-badge--neutral" v-if="!dashboard.event_info.has_payment">
              Gratuito
            </span>
            <span class="event-badge event-badge--info" v-else>
              Pago
            </span>
          </div>
        </div>

        <!-- <OnboardingBanner @start-tour="startTour" /> -->

        <!-- ROW 1 - Checklist (oculto temporariamente) -->
        <div class="checklist-card" data-tour="checklist" v-if="false && dashboard.checklist && !checklistDismissed">
          <div class="checklist-header" @click="checklistCollapsed = !checklistCollapsed">
            <div class="checklist-header-left">
              <i class="bx bx-check-shield checklist-icon"></i>
              <span class="checklist-title">Checklist</span>
              <span class="checklist-count">{{ dashboard.checklist.completed_count }}/{{ dashboard.checklist.total_count }} conclu&iacute;dos</span>
              <span class="checklist-pct" :class="checklistPctClass">{{ dashboard.checklist.percentage }}%</span>
            </div>
            <div class="checklist-header-right">
              <button class="checklist-tour-btn" @click.stop="startTour" title="Tour guiado">
                <i class="bx bx-play-circle"></i>
              </button>
              <button class="checklist-toggle" :class="{ 'is-collapsed': checklistCollapsed }">
                <i class="bx bx-chevron-down"></i>
              </button>
              <button class="checklist-dismiss" @click.stop="dismissChecklist" title="Dispensar checklist">
                <i class="bx bx-x"></i>
              </button>
            </div>
          </div>
          <div class="checklist-progress">
            <div class="checklist-progress-bar" :style="{ width: dashboard.checklist.percentage + '%' }" :class="checklistPctClass"></div>
          </div>
          <div class="checklist-body" v-show="!checklistCollapsed">
            <!-- Celebration state when 100% -->
            <div class="checklist-celebration" v-if="dashboard.checklist.percentage === 100">
              <i class="bx bx-check-shield checklist-celebration-icon"></i>
              <span class="checklist-celebration-text">Evento configurado! Tudo pronto.</span>
            </div>
            <template v-for="(phase, phaseIdx) in checklistPhases">
              <div class="checklist-phase-label" :key="'phase-' + phaseIdx">
                Fase {{ phase.phase }}: {{ phase.label }}
              </div>
              <component
                v-for="item in phase.items"
                :key="item.key"
                :is="areDependenciesMet(item) ? 'router-link' : 'div'"
                :to="areDependenciesMet(item) ? item.route : undefined"
                class="checklist-item"
                :class="{
                  'is-done': item.done,
                  'is-locked': !areDependenciesMet(item),
                  'is-next': !item.done && areDependenciesMet(item) && isFirstPending(item)
                }"
              >
                <span class="checklist-item-check">
                  <i class="bx" :class="getItemIcon(item)"></i>
                </span>
                <span class="checklist-item-body">
                  <span class="checklist-item-label">{{ item.label }}</span>
                  <span class="checklist-item-desc">{{ item.description }}</span>
                  <span class="checklist-item-locked-hint" v-if="!areDependenciesMet(item)">
                    Requer: {{ getDependencyLabels(item).join(', ') }}
                  </span>
                </span>
                <span class="checklist-item-cta" v-if="!item.done && areDependenciesMet(item)">
                  Configurar <i class="bx bx-right-arrow-alt"></i>
                </span>
                <i class="bx bx-chevron-right checklist-item-arrow" v-else-if="item.done"></i>
              </component>
            </template>
          </div>
        </div>

        <!-- ROW 2 - KPI Cards -->
        <div class="kpi-row">
          <div class="kpi-card" style="animation-delay: 0.05s">
            <div class="kpi-label">Total Inscritos</div>
            <div class="kpi-value primary">{{ formatNumber(dashboard.kpis.total_participants) }}</div>
          </div>
          <div class="kpi-card" style="animation-delay: 0.08s">
            <div class="kpi-label">Confirmados</div>
            <div class="kpi-value success">{{ formatNumber(dashboard.kpis.confirmed) }}</div>
          </div>
          <div class="kpi-card" style="animation-delay: 0.14s" v-if="dashboard.avg_time_to_confirm !== null && dashboard.avg_time_to_confirm !== undefined">
            <div class="kpi-label">Tempo m&eacute;dio confirmar</div>
            <div class="kpi-value warning">{{ formatAvgTime(dashboard.avg_time_to_confirm) }}</div>
          </div>
          <div class="kpi-card" style="animation-delay: 0.23s">
            <div class="kpi-label">Taxa de Confirmação</div>
            <div class="kpi-value info">{{ confirmationRate }}%</div>
          </div>
        </div>

        <!-- INSIGHTS TICKER -->
        <div v-if="dashboard.insights && dashboard.insights.length" class="insight-ticker">
          <div class="insight-ticker-track" :style="{ animationDuration: tickerDuration }">
            <div v-for="copy in 4" :key="copy" class="ticker-set">
              <div
                v-for="(ins, i) in dashboard.insights"
                :key="i"
                class="insight-chip"
                :class="`insight-chip--${ins.type}`"
              >
                <i :class="`bx ${ins.icon}`"></i>
                <span>{{ ins.text }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- ROW 2.5 - Funil de Status -->
        <StatusFunnel :period="selectedRange" :date-from="customDates.start" :date-to="customDates.end" />

        <!-- ROW 4a - Timeline + Meta do Evento (80/20) -->
        <div class="cards-grid cards-grid--timeline-goal">
          <EnhancedTimeline :period="selectedRange" :parent-date-from="customDates.start" :parent-date-to="customDates.end" />

          <!-- Meta do evento -->
          <div class="sa-card goal-card" v-if="customGoal || dashboard.expected_participants > 0">
            <div class="sa-card-header">
              <span class="sa-card-title">Meta do Evento</span>
              <div class="goal-header-right">
                <span
                  v-if="activeGoalProjection"
                  class="goal-status-badge"
                  :class="`goal-status-badge--${activeGoalProjection.status}`"
                >
                  {{ activeGoalProjection.status === 'on_track' ? 'No ritmo' : activeGoalProjection.status === 'warning' ? 'Atenção' : 'Em risco' }}
                </span>
                <button class="goal-action-btn goal-action-btn--outline goal-action-btn--icon" @click="openGoalModal" title="Definir Meta">
                  <i class="bx bx-target-lock"></i>
                </button>
              </div>
            </div>

            <!-- Period info (custom mode only) -->
            <div v-if="customGoal" class="goal-period-info">
              <span class="goal-period-dates">
                <i class="bx bx-calendar-range"></i>
                {{ formatDate(customGoal.start_date) }} → {{ formatDate(customGoal.end_date) }}
              </span>
              <span class="goal-custom-badge">Meta personalizada</span>
            </div>

            <div class="goal-chart-wrap">
              <apexchart
                type="radialBar"
                height="220"
                :options="activeGoalChartOptions"
                :series="activeGoalChartSeries"
              />
            </div>

            <!-- Projection stats -->
            <div v-if="activeGoalProjection" class="goal-projection">
              <div class="goal-proj-item">
                <span class="goal-proj-value">
                  {{ activeGoalProjection.velocity_per_day }}/dia
                  <i
                    v-if="requiredVelocity !== null"
                    v-b-tooltip.hover
                    :title="'Necessário: ' + requiredVelocity + '/dia para atingir a meta'"
                    class="bx bx-info-circle goal-velocity-info"
                  ></i>
                </span>
                <span class="goal-proj-label">Velocidade</span>
              </div>
              <div class="goal-proj-divider"></div>
              <div class="goal-proj-item">
                <span class="goal-proj-value">{{ activeGoalProjection.days_remaining }}d</span>
                <span class="goal-proj-label">Restantes</span>
              </div>
              <div class="goal-proj-divider"></div>
              <div class="goal-proj-item">
                <span class="goal-proj-value">~{{ activeGoalProjection.projected_rate }}%</span>
                <span class="goal-proj-label">Projeção</span>
              </div>
            </div>

            <!-- Alert message -->
            <div
              v-if="activeGoalProjection && activeGoalProjection.status !== 'on_track'"
              class="goal-alert"
              :class="`goal-alert--${activeGoalProjection.status}`"
            >
              <i class="bx bx-info-circle"></i>
              <span>{{ activeGoalProjection.message }}</span>
            </div>

            <div class="goal-footer">
              <div class="goal-footer-item">
                <span class="goal-footer-value goal-footer-value--green">{{ formatNumber(activeGoalCurrent) }}</span>
                <span class="goal-footer-label">{{ customGoal ? 'No período' : activeGoalMetricLabel }}</span>
              </div>
              <div class="goal-footer-divider"></div>
              <div class="goal-footer-item">
                <span class="goal-footer-value">{{ formatNumber(activeGoalTarget) }}</span>
                <span class="goal-footer-label">{{ customGoal ? 'Meta' : 'Esperados' }}</span>
              </div>
            </div>
          </div>

          <!-- Goal Modal -->
          <b-modal v-model="showGoalModal" title="Definir Meta personalizada" hide-footer centered size="md">
            <div class="goal-modal-body">
              <div class="goal-modal-field">
                <label class="goal-modal-label">Métrica</label>
                <div class="goal-metric-toggle">
                  <button
                    type="button"
                    class="goal-metric-btn"
                    :class="{ active: goalForm.metric === 'confirmed' }"
                    @click="goalForm.metric = 'confirmed'"
                  >
                    <i class="bx bx-check-circle"></i> Confirmados
                  </button>
                  <button
                    type="button"
                    class="goal-metric-btn"
                    :class="{ active: goalForm.metric === 'registered' }"
                    @click="goalForm.metric = 'registered'"
                  >
                    <i class="bx bx-user-plus"></i> Inscritos
                  </button>
                </div>
              </div>
              <div class="goal-modal-field">
                <label class="goal-modal-label">Meta de {{ goalForm.metric === 'registered' ? 'inscritos' : 'confirmados' }}</label>
                <input type="number" v-model.number="goalForm.target" class="form-control form-control-sm" placeholder="Ex: 900" min="1" />
              </div>
              <div class="goal-modal-row">
                <div class="goal-modal-field">
                  <label class="goal-modal-label">De</label>
                  <input type="date" v-model="goalForm.startDate" class="form-control form-control-sm" />
                </div>
                <div class="goal-modal-field">
                  <label class="goal-modal-label">Até</label>
                  <input type="date" v-model="goalForm.endDate" class="form-control form-control-sm" :min="goalForm.startDate" />
                </div>
              </div>
              <div v-if="goalFormError" class="goal-modal-error">{{ goalFormError }}</div>
              <div class="goal-modal-actions">
                <button v-if="customGoal" class="btn btn-sm goal-modal-reset-btn" @click="resetGoal(); showGoalModal = false">
                  <i class="bx bx-trash"></i> Remover Meta
                </button>
                <div class="goal-modal-actions-right">
                  <button class="btn btn-light btn-sm" @click="showGoalModal = false">Cancelar</button>
                  <button class="btn btn-primary btn-sm" @click="saveGoal" :disabled="goalSaving">
                    <b-spinner v-if="goalSaving" small class="mr-1"></b-spinner>
                    Salvar Meta
                  </button>
                </div>
              </div>

              <!-- Audit log -->
              <div v-if="customGoalHistory.length" class="goal-log">
                <div class="goal-log-title">Histórico de alterações</div>
                <div v-for="(entry, i) in [...customGoalHistory].reverse()" :key="i" class="goal-log-entry">
                  <span class="goal-log-badge" :class="`goal-log-badge--${entry.action}`">
                    {{ entry.action === 'created' ? 'Criado' : entry.action === 'updated' ? 'Editado' : 'Removido' }}
                  </span>
                  <div class="goal-log-info">
                    <span class="goal-log-user"><i class="bx bx-user"></i> {{ entry.user }}</span>
                    <span class="goal-log-at">{{ entry.at }}</span>
                  </div>
                  <div v-if="entry.target" class="goal-log-detail">
                    {{ formatNumber(entry.target) }} {{ entry.metric === 'registered' ? 'inscritos' : 'confirmados' }} &middot; {{ formatDate(entry.start_date) }} → {{ formatDate(entry.end_date) }}
                  </div>
                </div>
              </div>
            </div>
          </b-modal>
        </div>

        <!-- ROW 4 - Status x Grupos + Comportamento Temporal -->
        <div class="cards-grid cards-grid--2">
          <!-- Status x Grupo -->
          <div class="sa-card">
            <div class="sa-card-header">
              <span class="sa-card-title"><i class="bx bx-group mr-2" style="color:#556ee6"></i>Status x Grupo</span>
            </div>
            <template v-if="dashboard.status_by_group && dashboard.status_by_group.length">
              <apexchart
                type="bar"
                :height="Math.max(220, dashboard.status_by_group.length * 44)"
                :options="statusByGroupOptions"
                :series="statusByGroupSeries"
              />
            </template>
            <div v-else class="empty-state-card">
              <div class="empty-icon"><i class="bx bx-group"></i></div>
              <div class="empty-title">Sem dados de grupos</div>
              <div class="empty-desc">Crie grupos e associe participantes.</div>
            </div>
          </div>

          <!-- Comportamento Temporal -->
          <div class="sa-card sa-card--no-scroll">
            <div class="sa-card-header">
              <span class="sa-card-title"><i class="bx bx-time-five mr-2" style="color:#556ee6"></i>Comportamento Temporal</span>
              <div class="dash-tabs">
                <button class="dash-tab" :class="{ active: temporalView === 'hour' }" @click="temporalView = 'hour'">Por Hora</button>
                <button class="dash-tab" :class="{ active: temporalView === 'weekday' }" @click="temporalView = 'weekday'">Por Dia</button>
                <button class="dash-tab" :class="{ active: temporalView === 'heatmap' }" @click="temporalView = 'heatmap'">Heatmap</button>
              </div>
            </div>

            <template v-if="temporalView === 'hour'">
              <template v-if="dashboard.registrations_by_hour && dashboard.registrations_by_hour.length">
                <apexchart type="bar" height="240" :options="regByHourOptions" :series="regByHourSeries" />
              </template>
              <div v-else class="empty-state-card">
                <div class="empty-icon"><i class="bx bx-time-five"></i></div>
                <div class="empty-title">Sem dados de hor&aacute;rio</div>
              </div>
            </template>

            <template v-else-if="temporalView === 'weekday'">
              <template v-if="dashboard.by_weekday && dashboard.by_weekday.length">
                <apexchart type="bar" height="240" :options="byWeekdayOptions" :series="byWeekdaySeries" />
              </template>
              <div v-else class="empty-state-card">
                <div class="empty-icon"><i class="bx bx-calendar-week"></i></div>
                <div class="empty-title">Sem dados</div>
              </div>
            </template>

            <template v-else-if="temporalView === 'heatmap'">
              <div class="heatmap-metric-toggle">
                <button
                  class="hmtab"
                  :class="{ active: heatmapMetric === 'inscricoes' }"
                  @click="heatmapMetric = 'inscricoes'"
                >Inscrições</button>
                <button
                  class="hmtab hmtab--green"
                  :class="{ active: heatmapMetric === 'confirmacoes' }"
                  @click="heatmapMetric = 'confirmacoes'"
                >Confirmações</button>
              </div>
              <template v-if="heatmapHasData">
                <apexchart :key="heatmapMetric" type="heatmap" height="220" :options="heatmapOptions" :series="heatmapSeries" />
                <div v-if="heatmapPeakLabel" class="heatmap-insight">
                  <i class="bx bx-bulb"></i> {{ heatmapPeakLabel }}
                </div>
              </template>
              <div v-else class="empty-state-card">
                <div class="empty-icon"><i class="bx bx-grid-alt"></i></div>
                <div class="empty-title">Sem dados de heatmap</div>
              </div>
            </template>
          </div>
        </div>

        <!-- ROW 5 - Chance de Comparecimento -->
        <div class="sa-card risk-card" v-if="dashboard.attendance_risk && dashboard.attendance_risk.total > 0">
          <div class="sa-card-header">
            <span class="sa-card-title"><i class="bx bx-shield-quarter mr-2" style="color:#556ee6"></i>Chance de Comparecimento</span>
            <span class="sa-card-subtitle">{{ dashboard.attendance_risk.total }} confirmados analisados</span>
          </div>

          <!-- Tier bar -->
          <div class="risk-tier-bar">
            <div
              class="risk-tier-seg risk-tier-seg--high"
              :style="{ flex: dashboard.attendance_risk.high || 0.01 }"
              :title="`Alta: ${dashboard.attendance_risk.high}`"
            >
              <div v-if="dashboard.attendance_risk.high > 0" class="risk-tier-seg-inner">
                <span class="risk-seg-count">{{ dashboard.attendance_risk.high }}</span>
                <span class="risk-seg-label">Alta</span>
              </div>
            </div>
            <div
              class="risk-tier-seg risk-tier-seg--medium"
              :style="{ flex: dashboard.attendance_risk.medium || 0.01 }"
              :title="`Média: ${dashboard.attendance_risk.medium}`"
            >
              <div v-if="dashboard.attendance_risk.medium > 0" class="risk-tier-seg-inner">
                <span class="risk-seg-count">{{ dashboard.attendance_risk.medium }}</span>
                <span class="risk-seg-label">Média</span>
              </div>
            </div>
            <div
              class="risk-tier-seg risk-tier-seg--low"
              :style="{ flex: dashboard.attendance_risk.low || 0.01 }"
              :title="`Baixa: ${dashboard.attendance_risk.low}`"
            >
              <div v-if="dashboard.attendance_risk.low > 0" class="risk-tier-seg-inner">
                <span class="risk-seg-count">{{ dashboard.attendance_risk.low }}</span>
                <span class="risk-seg-label">Baixa</span>
              </div>
            </div>
          </div>

          <!-- Tier labels -->
          <div class="risk-tier-labels">
            <div class="risk-tier-label">
              <span class="risk-dot risk-dot--high"></span>
              <span class="risk-tier-count">{{ dashboard.attendance_risk.high }}</span>
              <span class="risk-tier-name">Alta</span>
              <span class="risk-tier-pct">{{ Math.round(dashboard.attendance_risk.high / dashboard.attendance_risk.total * 100) }}%</span>
            </div>
            <div class="risk-tier-label">
              <span class="risk-dot risk-dot--medium"></span>
              <span class="risk-tier-count">{{ dashboard.attendance_risk.medium }}</span>
              <span class="risk-tier-name">Média</span>
              <span class="risk-tier-pct">{{ Math.round(dashboard.attendance_risk.medium / dashboard.attendance_risk.total * 100) }}%</span>
            </div>
            <div class="risk-tier-label">
              <span class="risk-dot risk-dot--low"></span>
              <span class="risk-tier-count">{{ dashboard.attendance_risk.low }}</span>
              <span class="risk-tier-name">Baixa</span>
              <span class="risk-tier-pct">{{ Math.round(dashboard.attendance_risk.low / dashboard.attendance_risk.total * 100) }}%</span>
            </div>
          </div>

          <!-- At-risk list -->
          <div v-if="(dashboard.attendance_risk.at_risk && dashboard.attendance_risk.at_risk.length) || (dashboard.attendance_risk.needs_attention && dashboard.attendance_risk.needs_attention.length)" class="risk-list">
            <div class="risk-list-title">Precisam de atenção:</div>
            <div
              v-for="p in dashboard.attendance_risk.at_risk"
              :key="p.id"
              class="risk-person"
            >
              <div class="risk-person-avatar">{{ p.name.charAt(0).toUpperCase() }}</div>
              <div class="risk-person-info">
                <span class="risk-person-name">{{ p.name }}</span>
                <span class="risk-person-reason">{{ p.reason }}</span>
              </div>
              <span class="risk-tier-badge risk-tier-badge--low">Em risco</span>
            </div>
            <div
              v-for="p in dashboard.attendance_risk.needs_attention"
              :key="'m' + p.id"
              class="risk-person"
            >
              <div class="risk-person-avatar">{{ p.name.charAt(0).toUpperCase() }}</div>
              <div class="risk-person-info">
                <span class="risk-person-name">{{ p.name }}</span>
                <span class="risk-person-reason">{{ p.reason }}</span>
              </div>
              <span class="risk-tier-badge risk-tier-badge--medium">Atenção</span>
            </div>
          </div>
          <div v-else class="risk-all-good">
            <i class="bx bx-check-circle"></i>
            <span>Todos os confirmados têm boa probabilidade de comparecimento!</span>
          </div>
        </div>

        <!-- Detalhe por grupo -->
        <div class="capacity-block" v-if="dashboard.capacity || (dashboard.rc_items && dashboard.rc_items.length)">
          <div class="capacity-header">
            <span class="capacity-title"><i class="bx bx-category mr-2" style="color:#556ee6"></i>Detalhe por grupo</span>
            <span class="capacity-summary" v-if="dashboard.capacity && dashboard.capacity.limit">
              {{ formatNumber(dashboard.capacity.confirmed) }} / {{ formatNumber(dashboard.capacity.limit) }} confirmados
              <span class="capacity-pct" :class="capacityPctClass">{{ dashboard.capacity.percentage }}%</span>
            </span>
          </div>
          <!-- Barra geral -->
          <div v-if="dashboard.capacity && dashboard.capacity.limit" class="capacity-bar-wrap">
            <div class="capacity-bar-track">
              <div class="capacity-bar-fill" :style="{ width: Math.min(dashboard.capacity.percentage, 100) + '%' }" :class="capacityBarClass"></div>
            </div>
            <div class="capacity-meta">
              <span v-if="dashboard.capacity.remaining > 0" class="capacity-remaining">{{ formatNumber(dashboard.capacity.remaining) }} vagas restantes</span>
              <span v-else-if="dashboard.capacity.waiting_count > 0" class="capacity-waiting"><i class="bx bx-time-five"></i> {{ formatNumber(dashboard.capacity.waiting_count) }} na lista de espera</span>
              <span v-else class="capacity-full">Lotado</span>
            </div>
          </div>
          <!-- RC Items table -->
          <div v-if="dashboard.rc_items && dashboard.rc_items.length" class="rc-items-table-wrap">
            <table class="rc-items-table">
              <thead>
                <tr>
                  <th>Grupo / Identificador</th>
                  <th class="text-center">Total</th>
                  <th class="text-center rc-col--confirmed">Confirmados</th>
                  <th class="text-center rc-col--pending">Pendentes</th>
                  <th class="text-center rc-col--progress">Em Andamento</th>
                  <th class="text-center rc-col--undefined">A Definir</th>
                  <th class="text-center rc-col--declined">Declinados</th>
                  <th v-if="hasAnyLimit" class="text-center">Vagas</th>
                  <th v-if="hasAnyLimit" class="text-center">Ocup.</th>
                  <th v-if="hasAnyLimit" class="text-center">Restante</th>
                  <th class="text-center rc-col--confirmed">% Confirm.</th>
                  <th class="rc-col--expand"></th>
                </tr>
              </thead>
              <tbody>
                <template v-for="item in dashboard.rc_items">
                  <!-- Main row -->
                  <tr :key="item.id">
                    <td class="rc-item-name">
                      <span class="rc-item-identifier" v-if="item.identifier">{{ item.identifier }}</span>
                      {{ item.name }}
                    </td>
                    <td class="text-center"><span class="value-badge">{{ item.total }}</span></td>
                    <td class="text-center font-weight-bold" style="color:#34c38f">{{ item.confirmados }}</td>
                    <td class="text-center" style="color:#f1b44c">{{ item.pendentes }}</td>
                    <td class="text-center" style="color:#556ee6">{{ item.em_andamento }}</td>
                    <td class="text-center" style="color:#8b5cf6">{{ item.a_definir }}</td>
                    <td class="text-center" style="color:#f46a6a">{{ item.declinados }}</td>
                    <td v-if="hasAnyLimit" class="text-center text-muted">{{ item.limit > 0 ? item.limit : '—' }}</td>
                    <td v-if="hasAnyLimit" class="text-center">
                      <div v-if="item.percentage !== null" class="rc-occ-wrap">
                        <div class="rc-occ-bar">
                          <div class="rc-occ-fill" :style="{ width: Math.min(item.percentage, 100) + '%' }" :class="getRcBarClass(item.percentage)"></div>
                        </div>
                        <span class="rc-occ-pct" :class="getRcPctClass(item.percentage)">{{ item.percentage }}%</span>
                      </div>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td v-if="hasAnyLimit" class="text-center">
                      <span v-if="item.remaining !== null && item.remaining > 0" class="rc-remaining">{{ item.remaining }}</span>
                      <span v-else-if="item.waiting > 0" class="rc-waiting">+{{ item.waiting }} espera</span>
                      <span v-else-if="item.remaining === 0" class="rc-full">Lotado</span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td class="text-center">
                      <div class="mg-rate-inline">
                        <div class="mg-rate-bar mg-rate-bar--sm">
                          <div class="mg-rate-bar-fill" :style="{ width: item.confirmation_rate + '%' }" :class="item.confirmation_rate >= 70 ? 'fill-success' : item.confirmation_rate >= 40 ? 'fill-warning' : 'fill-danger'"></div>
                        </div>
                        <span class="font-weight-bold" :class="item.confirmation_rate >= 70 ? 'text-success' : item.confirmation_rate >= 40 ? 'text-warning' : 'text-danger'">{{ item.confirmation_rate }}%</span>
                      </div>
                    </td>
                    <td class="rc-col--expand">
                      <button
                        v-if="(item.pending_breakdown && item.pending_breakdown.length) || (item.companions && item.companions.total > 0)"
                        class="rc-expand-btn"
                        :class="{ active: expandedGroups[item.id] }"
                        @click="toggleGroup(item.id)"
                      >
                        <i class="bx" :class="expandedGroups[item.id] ? 'bx-chevron-up' : 'bx-chevron-down'"></i>
                      </button>
                    </td>
                  </tr>
                  <!-- Breakdown rows: one per sub-status -->
                  <template v-if="expandedGroups[item.id]">
                    <!-- Acompanhantes consolidados neste grupo (recorte do total) -->
                    <tr v-if="item.companions && item.companions.total > 0" :key="item.id + '-companions'" class="rc-breakdown-row rc-breakdown-row--companions">
                      <td class="rc-breakdown-name"><i class="bx bx-link-alt mr-1"></i>Acompanhante</td>
                      <td class="text-center rc-breakdown-count"><span class="value-badge">{{ item.companions.total }}</span></td>
                      <td class="text-center" style="color:#34c38f">{{ item.companions.confirmados }}</td>
                      <td class="text-center" style="color:#f1b44c">{{ item.companions.pendentes }}</td>
                      <td class="text-center" style="color:#556ee6">{{ item.companions.em_andamento }}</td>
                      <td class="text-center" style="color:#8b5cf6">{{ item.companions.a_definir }}</td>
                      <td class="text-center" style="color:#f46a6a">{{ item.companions.declinados }}</td>
                      <td v-if="hasAnyLimit"></td>
                      <td v-if="hasAnyLimit"></td>
                      <td v-if="hasAnyLimit"></td>
                      <td></td>
                      <td></td>
                    </tr>
                    <tr v-for="(sub, si) in item.pending_breakdown" :key="item.id + '-sub-' + si" class="rc-breakdown-row">
                      <td class="rc-breakdown-name">{{ sub.name }}</td>
                      <td></td>
                      <td></td>
                      <td class="text-center rc-breakdown-count" style="color:#f1b44c">{{ sub.count }}</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td v-if="hasAnyLimit"></td>
                      <td v-if="hasAnyLimit"></td>
                      <td v-if="hasAnyLimit"></td>
                      <td></td>
                      <td></td>
                    </tr>
                    <tr v-if="item.pending_no_sub_status > 0" :key="item.id + '-sub-none'" class="rc-breakdown-row rc-breakdown-row--none">
                      <td class="rc-breakdown-name">Sem classificação</td>
                      <td></td>
                      <td></td>
                      <td class="text-center rc-breakdown-count" style="color:#adb5bd">{{ item.pending_no_sub_status }}</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td v-if="hasAnyLimit"></td>
                      <td v-if="hasAnyLimit"></td>
                      <td v-if="hasAnyLimit"></td>
                      <td></td>
                      <td></td>
                    </tr>
                  </template>
                </template>
              </tbody>
              <tfoot>
                <tr class="rc-tfoot-row">
                  <td class="rc-tfoot-label">Total geral</td>
                  <td class="text-center font-weight-bold">{{ rcTotals.total }}</td>
                  <td class="text-center font-weight-bold" style="color:#34c38f">{{ rcTotals.confirmados }}</td>
                  <td class="text-center font-weight-bold" style="color:#f1b44c">{{ rcTotals.pendentes }}</td>
                  <td class="text-center font-weight-bold" style="color:#556ee6">{{ rcTotals.em_andamento }}</td>
                  <td class="text-center font-weight-bold" style="color:#8b5cf6">{{ rcTotals.a_definir }}</td>
                  <td class="text-center font-weight-bold" style="color:#f46a6a">{{ rcTotals.declinados }}</td>
                  <td v-if="hasAnyLimit" class="text-center text-muted font-weight-bold">{{ rcTotals.limit || '—' }}</td>
                  <td v-if="hasAnyLimit" class="text-center">
                    <div v-if="rcTotals.percentage !== null" class="rc-occ-wrap">
                      <div class="rc-occ-bar">
                        <div class="rc-occ-fill" :style="{ width: Math.min(rcTotals.percentage, 100) + '%' }" :class="getRcBarClass(rcTotals.percentage)"></div>
                      </div>
                      <span class="rc-occ-pct font-weight-bold" :class="getRcPctClass(rcTotals.percentage)">{{ rcTotals.percentage }}%</span>
                    </div>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td v-if="hasAnyLimit" class="text-center">
                    <span v-if="rcTotals.remaining !== null && rcTotals.remaining > 0" class="rc-remaining font-weight-bold">{{ rcTotals.remaining }}</span>
                    <span v-else-if="rcTotals.remaining === 0" class="rc-full">Lotado</span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td class="text-center">
                    <div class="mg-rate-inline">
                      <div class="mg-rate-bar mg-rate-bar--sm">
                        <div class="mg-rate-bar-fill" :style="{ width: rcTotals.confirmation_rate + '%' }" :class="rcTotals.confirmation_rate >= 70 ? 'fill-success' : rcTotals.confirmation_rate >= 40 ? 'fill-warning' : 'fill-danger'"></div>
                      </div>
                      <span class="font-weight-bold" :class="rcTotals.confirmation_rate >= 70 ? 'text-success' : rcTotals.confirmation_rate >= 40 ? 'text-warning' : 'text-danger'">{{ rcTotals.confirmation_rate }}%</span>
                    </div>
                  </td>
                  <td></td>
                </tr>
                <!-- Recorte: total de acompanhantes (subconjunto do Total geral) -->
                <tr v-if="companionsTotals.total > 0" class="rc-tfoot-companions">
                  <td class="rc-tfoot-companions-label"><i class="bx bx-link-alt mr-1"></i>Acompanhante</td>
                  <td class="text-center font-weight-bold">{{ companionsTotals.total }}</td>
                  <td class="text-center font-weight-bold" style="color:#34c38f">{{ companionsTotals.confirmados }}</td>
                  <td class="text-center font-weight-bold" style="color:#f1b44c">{{ companionsTotals.pendentes }}</td>
                  <td class="text-center font-weight-bold" style="color:#556ee6">{{ companionsTotals.em_andamento }}</td>
                  <td class="text-center font-weight-bold" style="color:#8b5cf6">{{ companionsTotals.a_definir }}</td>
                  <td class="text-center font-weight-bold" style="color:#f46a6a">{{ companionsTotals.declinados }}</td>
                  <td v-if="hasAnyLimit"></td>
                  <td v-if="hasAnyLimit"></td>
                  <td v-if="hasAnyLimit"></td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- ROW 5.1.b - Vagas por grupo (Vagas Inteligentes) -->
        <SmartVacancyGroups
          v-if="dashboard.smart_vacancy && dashboard.smart_vacancy.groups && dashboard.smart_vacancy.groups.length"
          :data="dashboard.smart_vacancy"
        />

        <!-- ROW 5.5 - Análise por Campo (dinâmica) -->
        <DynamicFieldChart :period="selectedRange" :date-from="customDates.start" :date-to="customDates.end" />

        <!-- ROW 6 - Jornadas Ativas -->
        <div class="sa-card hero-card" v-if="dashboard.journeys && dashboard.journeys.length">
          <div class="sa-card-header">
            <span class="sa-card-title"><i class="bx bx-git-branch mr-2" style="color:#556ee6"></i>Jornadas Ativas</span>
            <span class="sa-card-subtitle">{{ dashboard.journeys.length }} jornada{{ dashboard.journeys.length > 1 ? 's' : '' }}</span>
          </div>
          <div class="table-wrapper">
            <table class="modern-table">
              <thead>
                <tr>
                  <th>Jornada</th>
                  <th>Inscritos</th>
                  <th>Ativas</th>
                  <th>Completas</th>
                  <th>Falhas</th>
                  <th>Conclus&atilde;o</th>
                  <th style="width: 160px">Progresso</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(j, i) in dashboard.journeys" :key="'j'+i">
                  <td class="campaign-name">{{ j.journey_name }}</td>
                  <td><span class="value-badge">{{ j.total_enrolled }}</span></td>
                  <td>{{ j.active }}</td>
                  <td class="text-success font-weight-bold">{{ j.completed }}</td>
                  <td class="text-danger">{{ j.failed }}</td>
                  <td><span class="font-weight-bold">{{ j.completion_rate }}%</span></td>
                  <td>
                    <div class="progress-bar-wrap">
                      <div class="progress-bar-track">
                        <div class="progress-bar-fill progress-bar--success" :style="{ width: j.completion_rate + '%' }"></div>
                        <div class="progress-bar-fill progress-bar--danger" :style="{ width: j.fail_rate + '%' }"></div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ROW 6 - Campanhas Email + WhatsApp -->
        <div class="cards-grid cards-grid--2">
          <!-- Email -->
          <div class="sa-card">
            <div class="sa-card-header">
              <span class="sa-card-title"><i class="bx bx-mail-send mr-2" style="color:#556ee6"></i>Campanhas de Email</span>
            </div>
            <template v-if="dashboard.campaigns.email.summary.total_campaigns > 0">
              <!-- Summary KPIs -->
              <div class="mini-kpi-row">
                <div class="mini-kpi">
                  <div class="mini-kpi-val">{{ dashboard.campaigns.email.summary.total_campaigns }}</div>
                  <div class="mini-kpi-label">Campanhas</div>
                </div>
                <div class="mini-kpi">
                  <div class="mini-kpi-val">{{ dashboard.campaigns.email.summary.total_templates }}</div>
                  <div class="mini-kpi-label">Templates</div>
                </div>
                <div class="mini-kpi">
                  <div class="mini-kpi-val">{{ formatNumber(dashboard.campaigns.email.summary.total_sent) }}</div>
                  <div class="mini-kpi-label">Enviados</div>
                </div>
                <div class="mini-kpi">
                  <div class="mini-kpi-val text-success">{{ dashboard.campaigns.email.summary.open_rate }}%</div>
                  <div class="mini-kpi-label">Abertura</div>
                </div>
              </div>
              <!-- Rate bars -->
              <div class="rate-bars">
                <div class="rate-bar-item">
                  <div class="rate-bar-header">
                    <span class="rate-bar-label">Entrega</span>
                    <span class="rate-bar-value">{{ dashboard.campaigns.email.summary.delivery_rate }}%</span>
                  </div>
                  <div class="rate-bar-track"><div class="rate-bar-fill" :style="{ width: dashboard.campaigns.email.summary.delivery_rate + '%', background: '#34c38f' }"></div></div>
                </div>
                <div class="rate-bar-item">
                  <div class="rate-bar-header">
                    <span class="rate-bar-label">Visualiza&ccedil;&atilde;o</span>
                    <span class="rate-bar-value">{{ dashboard.campaigns.email.summary.open_rate }}%</span>
                  </div>
                  <div class="rate-bar-track"><div class="rate-bar-fill" :style="{ width: dashboard.campaigns.email.summary.open_rate + '%', background: '#556ee6' }"></div></div>
                </div>
                <div class="rate-bar-item">
                  <div class="rate-bar-header">
                    <span class="rate-bar-label">Cliques</span>
                    <span class="rate-bar-value">{{ dashboard.campaigns.email.summary.click_rate }}%</span>
                  </div>
                  <div class="rate-bar-track"><div class="rate-bar-fill" :style="{ width: dashboard.campaigns.email.summary.click_rate + '%', background: '#50a5f1' }"></div></div>
                </div>
              </div>
              <!-- Recent campaigns -->
              <div class="recent-section" v-if="dashboard.campaigns.email.recent.length">
                <div class="recent-title">&Uacute;ltimas campanhas</div>
                <div class="table-wrapper">
                  <table class="modern-table modern-table--compact">
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Enviados</th>
                        <th>Abertura</th>
                        <th>Cliques</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(c, i) in dashboard.campaigns.email.recent" :key="'e'+i">
                        <td class="campaign-name">{{ c.name }}</td>
                        <td>{{ formatNumber(c.sent) }}</td>
                        <td><span :class="c.open_rate >= 50 ? 'text-success' : 'text-warning'" class="font-weight-bold">{{ c.open_rate }}%</span></td>
                        <td>{{ c.click_rate }}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>
            <div v-else class="empty-state-card">
              <div class="empty-icon"><i class="bx bx-mail-send"></i></div>
              <div class="empty-title">Sem campanhas de email</div>
              <div class="empty-desc">Crie campanhas de email para ver m&eacute;tricas aqui.</div>
            </div>
          </div>

          <!-- WhatsApp -->
          <div class="sa-card">
            <div class="sa-card-header">
              <span class="sa-card-title"><i class="bx bxl-whatsapp mr-2" style="color:#34c38f"></i>Campanhas de WhatsApp</span>
            </div>
            <template v-if="dashboard.campaigns.whatsapp.summary.total_campaigns > 0">
              <!-- Summary KPIs -->
              <div class="mini-kpi-row">
                <div class="mini-kpi">
                  <div class="mini-kpi-val">{{ dashboard.campaigns.whatsapp.summary.total_campaigns }}</div>
                  <div class="mini-kpi-label">Campanhas</div>
                </div>
                <div class="mini-kpi">
                  <div class="mini-kpi-val">{{ dashboard.campaigns.whatsapp.summary.total_templates }}</div>
                  <div class="mini-kpi-label">Templates</div>
                </div>
                <div class="mini-kpi">
                  <div class="mini-kpi-val">{{ formatNumber(dashboard.campaigns.whatsapp.summary.total_sent) }}</div>
                  <div class="mini-kpi-label">Enviados</div>
                </div>
                <div class="mini-kpi">
                  <div class="mini-kpi-val text-success">{{ dashboard.campaigns.whatsapp.summary.open_rate }}%</div>
                  <div class="mini-kpi-label">Abertura</div>
                </div>
              </div>
              <!-- Rate bars -->
              <div class="rate-bars">
                <div class="rate-bar-item">
                  <div class="rate-bar-header">
                    <span class="rate-bar-label">Entrega</span>
                    <span class="rate-bar-value">{{ dashboard.campaigns.whatsapp.summary.delivery_rate }}%</span>
                  </div>
                  <div class="rate-bar-track"><div class="rate-bar-fill" :style="{ width: dashboard.campaigns.whatsapp.summary.delivery_rate + '%', background: '#34c38f' }"></div></div>
                </div>
                <div class="rate-bar-item">
                  <div class="rate-bar-header">
                    <span class="rate-bar-label">Visualiza&ccedil;&atilde;o</span>
                    <span class="rate-bar-value">{{ dashboard.campaigns.whatsapp.summary.open_rate }}%</span>
                  </div>
                  <div class="rate-bar-track"><div class="rate-bar-fill" :style="{ width: dashboard.campaigns.whatsapp.summary.open_rate + '%', background: '#556ee6' }"></div></div>
                </div>
              </div>
              <!-- Recent campaigns -->
              <div class="recent-section" v-if="dashboard.campaigns.whatsapp.recent.length">
                <div class="recent-title">&Uacute;ltimas campanhas</div>
                <div class="table-wrapper">
                  <table class="modern-table modern-table--compact">
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Enviados</th>
                        <th>Entregues</th>
                        <th>Abertura</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(c, i) in dashboard.campaigns.whatsapp.recent" :key="'w'+i">
                        <td class="campaign-name">{{ c.name }}</td>
                        <td>{{ formatNumber(c.sent) }}</td>
                        <td>{{ formatNumber(c.delivered) }}</td>
                        <td><span :class="c.open_rate >= 50 ? 'text-success' : 'text-warning'" class="font-weight-bold">{{ c.open_rate }}%</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>
            <div v-else class="empty-state-card">
              <div class="empty-icon"><i class="bx bxl-whatsapp"></i></div>
              <div class="empty-title">Sem campanhas de WhatsApp</div>
              <div class="empty-desc">Crie campanhas de WhatsApp para ver m&eacute;tricas aqui.</div>
            </div>
          </div>
        </div>

      </template>

      <!-- Empty State -->
      <div v-else class="empty-state-page">
        <i class="bx bx-bar-chart-alt-2"></i>
        <h5>Nenhum dado encontrado</h5>
        <p class="text-muted">Tente alterar o per&iacute;odo ou aguarde novas inscri&ccedil;&otilde;es.</p>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import appConfig from '@/app.config'
import http from '@/http'
import VueApexCharts from 'vue-apexcharts'
import OnboardingBanner from '@/modules/onboarding/components/OnboardingBanner.vue'
import { setupTour } from '@/modules/onboarding'
import EnhancedTimeline from './components/EnhancedTimeline.vue'
import StatusFunnel from './components/StatusFunnel.vue'
import DynamicFieldChart from './components/DynamicFieldChart.vue'
import SmartVacancyGroups from './components/SmartVacancyGroups.vue'
import { getCustomGoal, saveCustomGoal, deleteCustomGoal } from './services'

export default {
  name: 'EventDashboard',
  components: {
    Layout,
    apexchart: VueApexCharts,
    OnboardingBanner,
    EnhancedTimeline,
    StatusFunnel,
    DynamicFieldChart,
    SmartVacancyGroups
  },
  page: {
    title: 'Dashboard',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  data () {
    return {
      loading: false,
      dashboard: null,
      selectedRange: 'all',
      rangeOptions: [
        { label: '7d', value: '7d' },
        { label: '30d', value: '30d' },
        { label: '90d', value: '90d' },
        { label: 'Todos', value: 'all' }
      ],
      customDates: { start: null, end: null },
      showDatePicker: false,
      refreshInterval: null,
      lastUpdate: null,
      elapsedTime: 0,
      elapsedInterval: null,
      statusColors: {
        a_definir: '#8b5cf6',
        em_andamento: '#556ee6',
        pendente: '#f1b44c',
        confirmado: '#34c38f',
        declinado: '#f46a6a'
      },
      checklistCollapsed: false,
      checklistDismissed: localStorage.getItem('dashChecklistDismissed') === 'true',
      lpCopied: false,
      geoView: 'state',
      temporalView: 'heatmap',
      heatmapMetric: 'inscricoes',
      expandedGroups: {},
      customGoal: null,
      customGoalProgress: null,
      customGoalHistory: [],
      showGoalModal: false,
      goalForm: { target: '', startDate: '', endDate: '', metric: 'confirmed' },
      goalSaving: false,
      goalFormError: null
    }
  },
  computed: {
    timeAgo () {
      if (!this.lastUpdate) return '0s'
      const seconds = this.elapsedTime
      if (seconds < 60) return `${seconds}s`
      const minutes = Math.floor(seconds / 60)
      if (minutes < 60) return `${minutes} min`
      return `${Math.floor(minutes / 60)}h`
    },

    // === EVENT INFO ===
    eventDateRange () {
      const info = this.dashboard?.event_info
      if (!info?.event_init_date) return null
      const fmt = (str) => {
        const d = new Date(str)
        return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
      }
      let range = fmt(info.event_init_date)
      if (info.event_end_date) range += ' - ' + fmt(info.event_end_date)
      return range
    },
    eventModeLabel () {
      const map = { presencial: 'Presencial', online: 'Online', hibrido: 'H\u00edbrido' }
      return map[this.dashboard?.event_info?.event_mode] || 'Presencial'
    },
    eventModeIcon () {
      const map = { presencial: 'bx-map-pin', online: 'bx-globe', hibrido: 'bx-git-merge' }
      return map[this.dashboard?.event_info?.event_mode] || 'bx-map-pin'
    },
    eventModeBadgeClass () {
      const map = { presencial: 'event-badge--primary', online: 'event-badge--info', hibrido: 'event-badge--warning' }
      return map[this.dashboard?.event_info?.event_mode] || 'event-badge--primary'
    },
    registrationStatusLabel () {
      return this.dashboard?.event_info?.registration_status === 1 ? 'Inscri\u00e7\u00f5es Abertas' : 'Inscri\u00e7\u00f5es Fechadas'
    },
    registrationStatusBadgeClass () {
      return this.dashboard?.event_info?.registration_status === 1 ? 'event-badge--success' : 'event-badge--danger'
    },

    // === CHECKLIST ===
    checklistPhases () {
      const items = this.dashboard?.checklist?.items || []
      const phases = []
      const grouped = {}
      items.forEach(item => {
        if (!grouped[item.phase]) {
          grouped[item.phase] = { phase: item.phase, label: item.phase_label, items: [] }
        }
        grouped[item.phase].items.push(item)
      })
      Object.keys(grouped).sort().forEach(k => phases.push(grouped[k]))
      return phases
    },
    checklistPctClass () {
      const pct = this.dashboard?.checklist?.percentage || 0
      if (pct >= 80) return 'pct-success'
      if (pct >= 40) return 'pct-warning'
      return 'pct-default'
    },

    goalRate () {
      const expected = this.dashboard?.expected_participants || 0
      const metric = this.dashboard?.goal_metric || 'confirmed'
      const current = metric === 'registered'
        ? (this.dashboard?.kpis?.total_participants || 0)
        : (this.dashboard?.kpis?.confirmed || 0)
      if (!expected) return 0
      return Math.min(Math.round(current / expected * 100), 100)
    },
    goalChartSeries () {
      return [this.goalRate]
    },
    goalChartOptions () {
      const expected = this.dashboard?.expected_participants || 0
      return {
        chart: { type: 'radialBar', toolbar: { show: false }, sparkline: { enabled: true } },
        plotOptions: {
          radialBar: {
            startAngle: -135,
            endAngle: 135,
            hollow: {
              size: '62%',
              background: 'transparent'
            },
            track: {
              background: '#eef0fb',
              strokeWidth: '100%',
              margin: 0
            },
            dataLabels: {
              name: {
                show: true,
                fontSize: '12px',
                fontWeight: 600,
                color: '#8b90a5',
                offsetY: 26,
                formatter: () => 'de ' + this.formatNumber(expected)
              },
              value: {
                fontSize: '28px',
                fontWeight: 700,
                color: '#1a1d26',
                offsetY: -4,
                formatter: (val) => val + '%'
              }
            }
          }
        },
        colors: ['#6366f1'],
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'light',
            type: 'horizontal',
            shadeIntensity: 0,
            gradientToColors: ['#34c38f'],
            stops: [0, 100]
          }
        },
        stroke: { lineCap: 'round' },
        labels: ['Meta']
      }
    },

    activeGoalTarget () {
      if (this.customGoal) return this.customGoal.target
      return this.dashboard?.expected_participants || 0
    },
    activeGoalCurrent () {
      if (this.customGoal && this.customGoalProgress) return this.customGoalProgress.count_in_period || this.customGoalProgress.confirmed_in_period
      const metric = this.dashboard?.goal_metric || 'confirmed'
      return metric === 'registered'
        ? (this.dashboard?.kpis?.total_participants || 0)
        : (this.dashboard?.kpis?.confirmed || 0)
    },
    activeGoalMetric () {
      if (this.customGoal) return this.customGoal.metric || 'confirmed'
      return this.dashboard?.goal_metric || 'confirmed'
    },
    activeGoalMetricLabel () {
      return this.activeGoalMetric === 'registered' ? 'Inscritos' : 'Confirmados'
    },
    requiredVelocity () {
      const target = this.activeGoalTarget
      const current = this.activeGoalCurrent
      const days = this.activeGoalProjection?.days_remaining
      if (!target || !days || days <= 0) return null
      const remaining = target - current
      if (remaining <= 0) return null
      return Math.ceil(remaining / days * 10) / 10
    },
    activeGoalRate () {
      if (this.customGoal && this.customGoalProgress) return this.customGoalProgress.percentage
      return this.goalRate
    },
    activeGoalProjection () {
      if (this.customGoal && this.customGoalProgress) return this.customGoalProgress
      return this.dashboard?.projection || null
    },
    activeGoalChartSeries () {
      return [this.activeGoalRate]
    },
    activeGoalChartOptions () {
      const target = this.activeGoalTarget
      const metricLabel = this.activeGoalMetric === 'registered' ? 'inscritos' : 'confirmados'
      return {
        chart: { type: 'radialBar', toolbar: { show: false }, sparkline: { enabled: true } },
        plotOptions: {
          radialBar: {
            startAngle: -135,
            endAngle: 135,
            hollow: { size: '62%', background: 'transparent' },
            track: { background: '#eef0fb', strokeWidth: '100%', margin: 0 },
            dataLabels: {
              name: {
                show: true,
                fontSize: '11px',
                fontWeight: 600,
                color: '#8b90a5',
                offsetY: 26,
                formatter: () => 'de ' + this.formatNumber(target) + ' ' + metricLabel
              },
              value: {
                fontSize: '28px',
                fontWeight: 700,
                color: '#1a1d26',
                offsetY: -4,
                formatter: (val) => val + '%'
              }
            }
          }
        },
        colors: ['#6366f1'],
        fill: {
          type: 'gradient',
          gradient: { shade: 'light', type: 'horizontal', shadeIntensity: 0, gradientToColors: ['#34c38f'], stops: [0, 100] }
        },
        stroke: { lineCap: 'round' },
        labels: ['Meta']
      }
    },

    confirmationRate () {
      const total = this.dashboard?.kpis?.total_participants || 0
      const confirmed = this.dashboard?.kpis?.confirmed || 0
      if (!total) return 0
      return Math.round(confirmed / total * 100)
    },

    // === WEEK COMPARISON ===
    weekTrendClass () {
      const g = this.dashboard?.week_comparison?.growth_pct
      if (g === null || g === undefined) return 'trend-neutral'
      return g > 0 ? 'trend-up' : g < 0 ? 'trend-down' : 'trend-neutral'
    },
    weekTrendIcon () {
      const g = this.dashboard?.week_comparison?.growth_pct
      if (g === null || g === undefined) return 'bx-minus'
      return g > 0 ? 'bx-up-arrow-alt' : g < 0 ? 'bx-down-arrow-alt' : 'bx-minus'
    },
    weekTrendLabel () {
      const wc = this.dashboard?.week_comparison
      if (!wc) return ''
      const g = wc.growth_pct
      if (g === null || g === undefined) return `${wc.last_7_days} esta semana`
      const sign = g > 0 ? '+' : ''
      return `${sign}${g}% (${wc.last_7_days})`
    },

    // === COUNTDOWN ===
    countdownIconClass () {
      const d = this.dashboard?.days_to_event
      if (d === null || d === undefined) return 'kpi-icon--info'
      if (d < 0) return 'kpi-icon--neutral'
      if (d <= 7) return 'kpi-icon--danger'
      if (d <= 30) return 'kpi-icon--warning'
      return 'kpi-icon--success'
    },
    countdownValueClass () {
      const d = this.dashboard?.days_to_event
      if (d === null || d === undefined) return ''
      if (d < 0) return 'neutral'
      if (d <= 7) return 'danger'
      if (d <= 30) return 'warning'
      return 'success'
    },
    countdownLabel () {
      const d = this.dashboard?.days_to_event
      if (d === null || d === undefined) return 'Dias para o evento'
      return d < 0 ? 'Evento encerrado' : 'Dias para o evento'
    },
    countdownValue () {
      const d = this.dashboard?.days_to_event
      if (d === null || d === undefined) return '—'
      if (d < 0) return Math.abs(d) + 'd atrás'
      if (d === 0) return 'Hoje!'
      return d + 'd'
    },

    // === CAPACITY ===
    capacityPctClass () {
      const p = this.dashboard?.capacity?.percentage
      if (!p) return ''
      if (p >= 90) return 'pct-danger'
      if (p >= 70) return 'pct-warning'
      return 'pct-success'
    },
    capacityBarClass () {
      const p = this.dashboard?.capacity?.percentage
      if (!p) return 'cap-bar--success'
      if (p >= 90) return 'cap-bar--danger'
      if (p >= 70) return 'cap-bar--warning'
      return 'cap-bar--success'
    },

    // === INSIGHTS TICKER ===
    tickerDuration () {
      const count = this.dashboard?.insights?.length || 1
      // ~9s por insight, mínimo 28s
      return Math.max(28, count * 9) + 's'
    },

    // === RC ITEMS ===
    hasAnyLimit () {
      return (this.dashboard?.rc_items || []).some(i => i.limit > 0)
    },

    // === RC ITEMS TOTALS (footer) ===
    rcTotals () {
      const items = this.dashboard?.rc_items || []
      const sum = (key) => items.reduce((s, i) => s + (i[key] || 0), 0)
      const total = sum('total')
      const confirmados = sum('confirmados')
      const limit = sum('limit')
      return {
        total,
        confirmados,
        pendentes: sum('pendentes'),
        em_andamento: sum('em_andamento'),
        a_definir: sum('a_definir'),
        declinados: sum('declinados'),
        limit,
        percentage: limit > 0 ? Math.round(confirmados / limit * 1000) / 10 : null,
        remaining: limit > 0 ? Math.max(0, limit - confirmados) : null,
        confirmation_rate: total > 0 ? Math.round(confirmados / total * 100) : 0
      }
    },

    // Total geral de acompanhantes (recorte do total) — soma o recorte de cada grupo.
    companionsTotals () {
      const items = this.dashboard?.rc_items || []
      const sum = (key) => items.reduce((s, i) => s + ((i.companions && i.companions[key]) || 0), 0)
      return {
        total: sum('total'),
        confirmados: sum('confirmados'),
        pendentes: sum('pendentes'),
        em_andamento: sum('em_andamento'),
        a_definir: sum('a_definir'),
        declinados: sum('declinados')
      }
    },

    // === GROUP RANKING ===
    groupRanking () {
      const groups = [...(this.dashboard?.status_by_group || [])]
      return groups.sort((a, b) => (b.confirmation_rate || 0) - (a.confirmation_rate || 0))
    },

    // === DEMOGRAPHICS ===
    genderChartSeries () {
      const data = this.dashboard?.demographics?.gender || []
      return data.map(d => d.total)
    },
    genderChartOptions () {
      const data = this.dashboard?.demographics?.gender || []
      const colors = ['#556ee6', '#f46a6a', '#34c38f', '#f1b44c', '#8b5cf6', '#50a5f1']
      return {
        chart: { type: 'donut', toolbar: { show: false } },
        labels: data.map(d => d.gender_label),
        colors: colors.slice(0, data.length),
        legend: { position: 'bottom', fontSize: '12px' },
        dataLabels: { enabled: true, formatter: (val) => val.toFixed(1) + '%' },
        plotOptions: { pie: { donut: { size: '60%' } } },
        tooltip: { y: { formatter: (val) => val + ' inscritos' } }
      }
    },
    ageRangesHasData () {
      return (this.dashboard?.demographics?.age_ranges || []).some(a => a.total > 0)
    },
    ageRangesSeries () {
      return [{ name: 'Inscritos', data: (this.dashboard?.demographics?.age_ranges || []).map(a => a.total) }]
    },
    ageRangesOptions () {
      return {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 3, columnWidth: '60%' } },
        colors: ['#556ee6'],
        xaxis: {
          categories: (this.dashboard?.demographics?.age_ranges || []).map(a => a.label),
          labels: { style: { fontSize: '11px', colors: '#8b90a5' } }
        },
        yaxis: { labels: { style: { fontSize: '11px', colors: '#8b90a5' } } },
        grid: { borderColor: '#f1f3f5', strokeDashArray: 4 },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (val) => val + ' inscritos' } }
      }
    },

    // === BY WEEKDAY ===
    byWeekdaySeries () {
      return [{ name: 'Inscrições', data: (this.dashboard?.by_weekday || []).map(d => d.total) }]
    },
    byWeekdayOptions () {
      return {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 3, columnWidth: '55%' } },
        colors: ['#34c38f'],
        xaxis: {
          categories: (this.dashboard?.by_weekday || []).map(d => d.day),
          labels: { style: { fontSize: '12px', colors: '#8b90a5' } }
        },
        yaxis: { labels: { style: { fontSize: '11px', colors: '#8b90a5' } } },
        grid: { borderColor: '#f1f3f5', strokeDashArray: 4 },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (val) => val + ' inscrições' } }
      }
    },

    // === STATUS x GROUP (stacked horizontal bar) ===
    statusByGroupSeries () {
      const data = this.dashboard.status_by_group || []
      return [
        { name: 'A Definir', data: data.map(d => d.a_definir) },
        { name: 'Em Andamento', data: data.map(d => d.em_andamento) },
        { name: 'Pendente', data: data.map(d => d.pendente) },
        { name: 'Confirmado', data: data.map(d => d.confirmado) },
        { name: 'Declinado', data: data.map(d => d.declinado) }
      ]
    },
    statusByGroupOptions () {
      return {
        chart: { type: 'bar', stacked: true, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '60%', borderRadius: 2 } },
        colors: [this.statusColors.a_definir, this.statusColors.em_andamento, this.statusColors.pendente, this.statusColors.confirmado, this.statusColors.declinado],
        xaxis: {
          categories: (this.dashboard.status_by_group || []).map(d => d.group_name),
          labels: { style: { fontSize: '11px', colors: '#8b90a5' } }
        },
        yaxis: { labels: { style: { fontSize: '12px', fontWeight: 600, colors: ['#4a5068'] }, maxWidth: 120 } },
        grid: { borderColor: '#f1f3f5', strokeDashArray: 4 },
        legend: { position: 'bottom', fontSize: '11px', fontWeight: 600 },
        dataLabels: { enabled: false },
        tooltip: { shared: true, intersect: false }
      }
    },

    // === HEATMAP (day × hour) ===
    heatmapSeries () {
      if (this.heatmapMetric === 'confirmacoes') {
        return this.dashboard?.heatmap_confirmed_by_day || []
      }
      return this.dashboard?.heatmap_by_day || []
    },
    heatmapHasData () {
      return this.heatmapSeries.some(s => s.data.some(d => d.y > 0))
    },
    heatmapPeakLabel () {
      let peak = null
      const dayNames = { Seg: 'Segunda', Ter: 'Terça', Qua: 'Quarta', Qui: 'Quinta', Sex: 'Sexta', Sáb: 'Sábado', Dom: 'Domingo' }
      ;(this.heatmapSeries || []).forEach(day => {
        day.data.forEach(cell => {
          if (!peak || cell.y > peak.y) peak = { day: day.name, hour: cell.x, y: cell.y }
        })
      })
      if (!peak || peak.y === 0) return null
      const noun = this.heatmapMetric === 'confirmacoes' ? 'confirmações' : 'inscrições'
      return `${dayNames[peak.day] || peak.day} às ${peak.hour} é o pico de ${noun}`
    },
    heatmapOptions () {
      const isConfirm = this.heatmapMetric === 'confirmacoes'
      const tooltip = isConfirm
        ? { y: { formatter: (val) => val > 0 ? `${val} confirm${val === 1 ? 'ação' : 'ações'}` : 'Nenhuma' } }
        : { y: { formatter: (val) => val > 0 ? `${val} inscriç${val === 1 ? 'ão' : 'ões'}` : 'Nenhuma' } }

      const colorRanges = isConfirm
        ? [
          { from: 0, to: 0, color: '#f1f3f8', name: 'Nenhuma' },
          { from: 1, to: 2, color: '#bbf7d0', name: 'Baixa' },
          { from: 3, to: 6, color: '#4ade80', name: 'Média' },
          { from: 7, to: 9999, color: '#16a34a', name: 'Alta' }
        ]
        : [
          { from: 0, to: 0, color: '#f1f3f8', name: 'Nenhuma' },
          { from: 1, to: 2, color: '#c7d2fe', name: 'Baixa' },
          { from: 3, to: 6, color: '#818cf8', name: 'Média' },
          { from: 7, to: 9999, color: '#4338ca', name: 'Alta' }
        ]

      return {
        chart: { type: 'heatmap', toolbar: { show: false }, animations: { enabled: false } },
        dataLabels: { enabled: false },
        colors: [isConfirm ? '#34c38f' : '#556ee6'],
        plotOptions: {
          heatmap: {
            shadeIntensity: 0.9,
            useFillColorAsStroke: false,
            colorScale: { ranges: colorRanges }
          }
        },
        xaxis: {
          type: 'category',
          labels: { style: { fontSize: '8px', colors: '#8b90a5' }, rotate: -45 },
          tooltip: { enabled: false }
        },
        yaxis: { labels: { style: { fontSize: '11px', colors: '#8b90a5' } } },
        grid: { padding: { right: 0 } },
        legend: {
          show: true,
          position: 'bottom',
          fontSize: '10px',
          labels: { colors: '#8b90a5' }
        },
        tooltip
      }
    },

    // === REGISTRATIONS BY HOUR (vertical bar) ===
    regByHourSeries () {
      const hours = this.dashboard.registrations_by_hour || []
      const data = new Array(24).fill(0)
      hours.forEach(h => { data[h.hour] = h.total })
      return [{ name: 'Inscricoes', data }]
    },
    regByHourOptions () {
      return {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: {
          bar: {
            columnWidth: '60%',
            borderRadius: 3,
            colors: {
              ranges: [
                { from: 0, to: 0, color: '#e9ecef' }
              ]
            }
          }
        },
        colors: ['#556ee6'],
        xaxis: {
          categories: Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}h`),
          labels: { style: { fontSize: '9px', colors: '#8b90a5' } }
        },
        yaxis: { labels: { style: { fontSize: '11px', colors: '#8b90a5' } } },
        grid: { borderColor: '#f1f3f5', strokeDashArray: 4 },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (val) => val + ' inscricoes' } }
      }
    }
  },

  mounted () {
    this.loadData()
    this.loadCustomGoal()
    this.startAutoRefresh()
  },
  beforeDestroy () {
    this.stopAutoRefresh()
  },

  methods: {
    async loadData () {
      this.loading = true
      try {
        const params = {}
        if (this.selectedRange === 'custom' && this.customDates.start && this.customDates.end) {
          params.start_date = this.customDates.start
          params.end_date = this.customDates.end
        } else {
          params.period = this.selectedRange
        }
        const response = await http.get('/admin/dashboard', { params })
        this.dashboard = response.data
        this.lastUpdate = Date.now()
        this.elapsedTime = 0
      } catch (error) {
        console.error('Erro ao carregar dashboard:', error)
      } finally {
        this.loading = false
      }
    },

    selectRange (range) {
      this.selectedRange = range
      this.customDates = { start: null, end: null }
      this.showDatePicker = false
      this.loadData()
    },

    applyCustomDates () {
      if (this.customDates.start && this.customDates.end) {
        this.selectedRange = 'custom'
        this.showDatePicker = false
        this.loadData()
      }
    },

    clearFilters () {
      this.customDates = { start: null, end: null }
      this.selectedRange = 'all'
      this.showDatePicker = false
      this.loadData()
    },

    startAutoRefresh () {
      this.refreshInterval = setInterval(() => { this.loadData() }, 120000)
      this.elapsedInterval = setInterval(() => { this.elapsedTime++ }, 1000)
    },

    stopAutoRefresh () {
      if (this.refreshInterval) clearInterval(this.refreshInterval)
      if (this.elapsedInterval) clearInterval(this.elapsedInterval)
    },

    formatNumber (num) {
      if (num === null || num === undefined) return '0'
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
      return num.toString()
    },

    formatDate (dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr + 'T00:00:00')
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
    },

    copyLpUrl () {
      const url = this.dashboard?.event_info?.lp_url
      if (!url) return
      navigator.clipboard.writeText(url).then(() => {
        this.lpCopied = true
        setTimeout(() => { this.lpCopied = false }, 2000)
      })
    },

    getInitials (name) {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
    },

    getAvatarColor (name) {
      const colors = ['#556ee6', '#34c38f', '#f1b44c', '#50a5f1', '#f46a6a', '#8b5cf6']
      let hash = 0
      for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
      return colors[Math.abs(hash) % colors.length]
    },

    dismissChecklist () {
      this.checklistDismissed = true
      localStorage.setItem('dashChecklistDismissed', 'true')
    },

    startTour () {
      this.$tour.start(setupTour)
    },

    areDependenciesMet (item) {
      if (!item.depends_on || !item.depends_on.length) return true
      const items = this.dashboard?.checklist?.items || []
      return item.depends_on.every(dep => {
        const depItem = items.find(i => i.key === dep)
        return depItem && depItem.done
      })
    },

    isFirstPending (item) {
      const items = this.dashboard?.checklist?.items || []
      const firstPending = items.find(i => !i.done && this.areDependenciesMet(i))
      return firstPending && firstPending.key === item.key
    },

    getItemIcon (item) {
      if (item.done) return 'bx-check-circle'
      if (!this.areDependenciesMet(item)) return 'bx-lock-alt'
      return 'bx-circle'
    },

    formatAvgTime (hours) {
      if (hours === null || hours === undefined) return '—'
      if (hours < 1) return '< 1h'
      if (hours < 24) return Math.round(hours) + 'h'
      const days = Math.floor(hours / 24)
      const remH = Math.round(hours % 24)
      return remH > 0 ? `${days}d ${remH}h` : `${days}d`
    },

    toggleGroup (id) {
      this.$set(this.expandedGroups, id, !this.expandedGroups[id])
    },

    getRcBarClass (pct) {
      if (pct >= 90) return 'rc-bar--danger'
      if (pct >= 70) return 'rc-bar--warning'
      return 'rc-bar--success'
    },

    getRcPctClass (pct) {
      if (pct >= 90) return 'text-danger'
      if (pct >= 70) return 'text-warning'
      return 'text-success'
    },

    getDependencyLabels (item) {
      if (!item.depends_on || !item.depends_on.length) return []
      const items = this.dashboard?.checklist?.items || []
      return item.depends_on
        .map(dep => {
          const depItem = items.find(i => i.key === dep)
          return depItem ? depItem.label : dep
        })
        .filter(label => {
          const depItem = (this.dashboard?.checklist?.items || []).find(i => i.label === label)
          return depItem && !depItem.done
        })
    },

    async loadCustomGoal () {
      try {
        const { data } = await getCustomGoal()
        this.customGoal = data.goal
        this.customGoalProgress = data.progress
        this.customGoalHistory = data.history || []
      } catch (e) {
        // silent
      }
    },

    openGoalModal () {
      if (this.customGoal) {
        this.goalForm = {
          target: this.customGoal.target,
          startDate: this.customGoal.start_date,
          endDate: this.customGoal.end_date,
          metric: this.customGoal.metric || 'confirmed'
        }
      } else {
        this.goalForm = { target: '', startDate: '', endDate: '', metric: this.dashboard?.goal_metric || 'confirmed' }
      }
      this.goalFormError = null
      this.showGoalModal = true
    },

    async saveGoal () {
      this.goalFormError = null
      if (!this.goalForm.target || !this.goalForm.startDate || !this.goalForm.endDate) {
        this.goalFormError = 'Preencha todos os campos.'
        return
      }
      this.goalSaving = true
      try {
        await saveCustomGoal({
          target: this.goalForm.target,
          start_date: this.goalForm.startDate,
          end_date: this.goalForm.endDate,
          metric: this.goalForm.metric
        })
        await this.loadCustomGoal()
        this.showGoalModal = false
      } catch (e) {
        this.goalFormError = 'Erro ao salvar meta.'
      } finally {
        this.goalSaving = false
      }
    },

    async resetGoal () {
      try {
        await deleteCustomGoal()
        this.customGoal = null
        this.customGoalProgress = null
        await this.loadCustomGoal()
      } catch (e) {
        // silent
      }
    }
  }
}
</script>

<style scoped>
@keyframes slideUp {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

/* === HEADER === */
.sa-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 16px;
}

.sa-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a1d26;
  margin: 0 0 1px 0;
}

.sa-subtitle {
  color: #adb5bd;
  font-weight: 400;
  font-size: 14px;
  margin: 0;
}

.sa-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* === TABS === */
.sa-tabs {
  display: flex;
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
  height: 38px;
  align-items: center;
}

.sa-tab {
  background: none;
  border: none;
  padding: 4px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #8b90a5;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.sa-tab:hover {
  color: #1a1d26;
  background: #f5f6fa;
}

.sa-tab.active {
  background: #556ee6;
  color: #fff;
  box-shadow: 0 2px 8px rgba(85, 110, 230, 0.3);
}

.sa-tab-sep {
  width: 1px;
  height: 20px;
  background: #dee2e6;
  margin: 0 4px;
}

.sa-date-picker-wrap {
  position: relative;
}

.sa-date-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  box-shadow: 0 8px 24px rgba(26, 29, 38, 0.12);
  padding: 16px;
  z-index: 200;
  min-width: 220px;
}

.sa-date-dropdown-label {
  font-size: 11px;
  font-weight: 600;
  color: #8b90a5;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
  display: block;
}

.sa-date-dropdown-label + .sa-date-dropdown-label {
  margin-top: 10px;
}

.sa-date-dropdown-input {
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  font-size: 13px;
  color: #1a1d26;
  outline: none;
  transition: border-color 0.2s;
  margin-bottom: 4px;
}

.sa-date-dropdown-input:focus {
  border-color: #556ee6;
}

.sa-date-dropdown-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.sa-date-dropdown-btn {
  flex: 1;
  padding: 7px 12px;
  border: none;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.sa-date-dropdown-btn.cancel {
  background: #f5f6fa;
  color: #8b90a5;
}

.sa-date-dropdown-btn.cancel:hover { background: #eaecf2; }

.sa-date-dropdown-btn.apply {
  background: #556ee6;
  color: #fff;
}

.sa-date-dropdown-btn.apply:hover { background: #3b5bdb; }

/* === KPI ROW === */
.kpi-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 24px;
}

.kpi-card {
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  padding: 18px 20px;
  box-shadow: 0 1px 3px rgba(26, 29, 38, 0.04);
  animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  opacity: 0;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.kpi-card:hover {
  border-color: #d4d8e3;
  box-shadow: 0 4px 16px rgba(26, 29, 38, 0.06);
}

.kpi-icon-wrap {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.kpi-icon--primary { background: rgba(85, 110, 230, 0.1); color: #556ee6; }
.kpi-icon--success { background: rgba(52, 195, 143, 0.1); color: #34c38f; }
.kpi-icon--info { background: rgba(80, 165, 241, 0.1); color: #50a5f1; }
.kpi-icon--danger { background: rgba(244, 106, 106, 0.1); color: #f46a6a; }

.kpi-body { flex: 1; min-width: 0; }

.kpi-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8b90a5;
  font-weight: 600;
  margin-bottom: 10px;
}

.kpi-value {
  font-size: 24px;
  font-weight: 700;
  color: #1a1d26;
  line-height: 1;
}

.kpi-value.primary { color: #556ee6; }
.kpi-value.success { color: #34c38f; }
.kpi-value.danger { color: #f46a6a; }
.kpi-value.info { color: #50a5f1; }

/* === DASH TABS (filtros internos de cards) === */
.dash-tabs {
  display: flex;
  background: #f5f6fa;
  border-radius: 6px;
  padding: 2px;
  gap: 2px;
}
.dash-tab {
  background: none;
  border: none;
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 600;
  color: #8b90a5;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.dash-tab:hover { color: #1a1d26; }
.dash-tab.active {
  background: #fff;
  color: #556ee6;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

/* === CARDS GRID === */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.cards-grid--2 { grid-template-columns: repeat(2, 1fr); }
.cards-grid--funnel-timeline { grid-template-columns: 3fr 7fr; }
.cards-grid--timeline-goal { grid-template-columns: 7fr 3fr; }

/* === CARD === */
.sa-card {
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(26, 29, 38, 0.04);
  transition: border-color 0.2s, box-shadow 0.2s;
  min-height: 280px;
  max-height: 520px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
  margin-bottom: 24px;
}

.cards-grid .sa-card { margin-bottom: 0; }

.sa-card::-webkit-scrollbar { width: 6px; }
.sa-card::-webkit-scrollbar-track { background: transparent; }
.sa-card::-webkit-scrollbar-thumb { background-color: #d0d5dd; border-radius: 3px; }

.sa-card:hover {
  border-color: #d4d8e3;
  box-shadow: 0 4px 16px rgba(26, 29, 38, 0.06);
}

.sa-card--no-scroll {
  overflow: visible;
  max-height: none;
}

.sa-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.sa-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1d26;
  display: flex;
  align-items: center;
}

.sa-card-subtitle {
  font-size: 12px;
  color: #8b90a5;
  font-weight: 500;
}

/* === META DO EVENTO === */
.goal-card {
  min-height: auto;
  max-height: none;
}
.goal-chart-wrap {
  position: relative;
  margin: -8px 0 0;
}
.goal-chart-wrap ::v-deep .apexcharts-datalabel-label {
  text-transform: none !important;
}
.goal-chart-wrap ::v-deep .apexcharts-datalabel-label tspan {
  text-transform: none !important;
}

.goal-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 12px 16px 4px;
  border-top: 1px solid #f0f2f8;
  margin-top: 12px;
}
.goal-footer-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 0 16px;
}
.goal-footer-divider {
  width: 1px;
  height: 32px;
  background: #eaecf2;
  flex-shrink: 0;
}
.goal-footer-value {
  font-size: 18px;
  font-weight: 700;
  color: #1a1d26;
  line-height: 1;
}
.goal-footer-value--green { color: #34c38f; }
.goal-footer-label {
  font-size: 10px;
  font-weight: 600;
  color: #8b90a5;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* === HERO CARD (full width) === */
.hero-card {
  min-height: auto;
  max-height: none;
  overflow: visible;
}

/* === TABLE === */
.table-wrapper { overflow-x: auto; }

.modern-table {
  width: 100%;
  font-size: 13px;
}

.modern-table thead th {
  font-weight: 600;
  color: #8b90a5;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 8px 12px;
  border-bottom: 2px solid #f1f3f5;
  white-space: nowrap;
}

.modern-table tbody td {
  padding: 12px;
  border-bottom: 1px solid #f8f9fa;
}

.modern-table tbody tr:hover { background: #f8f9fa; }

.modern-table--compact thead th { padding: 6px 10px; }
.modern-table--compact tbody td { padding: 8px 10px; }

.campaign-name {
  font-weight: 500;
  color: #1a1d26;
}

.value-badge {
  display: inline-block;
  background: #e8f4fd;
  color: #556ee6;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 12px;
}

/* === PROGRESS BAR (jornadas) === */
.progress-bar-wrap { width: 100%; }

.progress-bar-track {
  display: flex;
  height: 8px;
  background: #f5f6fa;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  transition: width 0.8s cubic-bezier(0.16, 1, 0.3, 1);
}

.progress-bar--success { background: #34c38f; }
.progress-bar--danger { background: #f46a6a; }

/* === MINI KPI ROW (campanhas) === */
.mini-kpi-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 18px;
}

.mini-kpi {
  text-align: center;
  padding: 10px 6px;
  background: #f8f9fc;
  border-radius: 6px;
}

.mini-kpi-val {
  font-size: 18px;
  font-weight: 700;
  color: #1a1d26;
  line-height: 1.2;
}

.mini-kpi-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8b90a5;
  font-weight: 600;
  margin-top: 2px;
}

/* === RATE BARS (campanhas) === */
.rate-bars {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
}

.rate-bar-item { }

.rate-bar-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.rate-bar-label {
  font-size: 12px;
  font-weight: 600;
  color: #4a5068;
}

.rate-bar-value {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  font-weight: 700;
  color: #1a1d26;
}

.rate-bar-track {
  width: 100%;
  height: 6px;
  background: #f5f6fa;
  border-radius: 3px;
  overflow: hidden;
}

.rate-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.8s cubic-bezier(0.16, 1, 0.3, 1);
}

.recent-section {
  border-top: 1px solid #f1f3f5;
  padding-top: 14px;
}

.recent-title {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8b90a5;
  font-weight: 600;
  margin-bottom: 10px;
}

/* === EVENT INFO CARD === */
.event-info-card {
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  padding: 24px 28px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(26, 29, 38, 0.04);
}

.event-info-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 14px;
  gap: 16px;
}

.event-info-name {
  font-size: 22px;
  font-weight: 700;
  color: #1a1d26;
  margin: 0;
  line-height: 1.3;
}

.event-info-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.btn-lp {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  background: #556ee6;
  color: #fff;
}

.btn-lp:hover {
  background: #4458c9;
  color: #fff;
  text-decoration: none;
}

.btn-lp--outline {
  background: #fff;
  color: #556ee6;
  border: 1px solid #556ee6;
}

.btn-lp--outline:hover {
  background: #f0f2ff;
  color: #556ee6;
}

.event-info-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-bottom: 14px;
}

.event-info-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #4a5068;
  font-weight: 500;
}

.event-info-meta-item i {
  font-size: 16px;
  color: #8b90a5;
}
.event-info-meta-item--muted { color: #b0b5c8; font-style: italic; }

.event-info-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.event-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 5px;
  letter-spacing: 0.02em;
}

.event-badge--primary { background: rgba(85, 110, 230, 0.1); color: #556ee6; }
.event-badge--success { background: rgba(52, 195, 143, 0.1); color: #2ba57a; }
.event-badge--danger { background: rgba(244, 106, 106, 0.1); color: #f46a6a; }
.event-badge--warning { background: rgba(241, 180, 76, 0.1); color: #c08a1e; }
.event-badge--info { background: rgba(80, 165, 241, 0.1); color: #3a8fd4; }
.event-badge--neutral { background: #f5f6fa; color: #4a5068; }

/* === CHECKLIST === */
.checklist-card {
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(26, 29, 38, 0.04);
  overflow: hidden;
}

.checklist-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
}

.checklist-header:hover {
  background: #f8f9fc;
}

.checklist-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.checklist-icon {
  font-size: 20px;
  color: #556ee6;
}

.checklist-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1d26;
}

.checklist-count {
  font-size: 12px;
  color: #8b90a5;
  font-weight: 500;
}

.checklist-pct {
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
}

.pct-default { background: #f5f6fa; color: #8b90a5; }
.pct-warning { background: rgba(241, 180, 76, 0.12); color: #c08a1e; }
.pct-success { background: rgba(52, 195, 143, 0.12); color: #2ba57a; }

.checklist-header-right {
  display: flex;
  align-items: center;
  gap: 4px;
}

.checklist-toggle,
.checklist-dismiss {
  background: none;
  border: none;
  cursor: pointer;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: #8b90a5;
  font-size: 18px;
  transition: all 0.2s;
}

.checklist-toggle:hover,
.checklist-dismiss:hover {
  background: #f0f2ff;
  color: #556ee6;
}

.checklist-toggle i {
  transition: transform 0.25s;
}

.checklist-toggle.is-collapsed i {
  transform: rotate(-90deg);
}

.checklist-progress {
  height: 3px;
  background: #f1f3f5;
}

.checklist-progress-bar {
  height: 100%;
  border-radius: 2px;
  transition: width 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

.checklist-progress-bar.pct-default { background: #8b90a5; }
.checklist-progress-bar.pct-warning { background: #f1b44c; }
.checklist-progress-bar.pct-success { background: #34c38f; }

.checklist-body {
  padding: 8px 12px 12px;
}

.checklist-phase-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #8b90a5;
  padding: 10px 12px 4px;
}

.checklist-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 6px;
  text-decoration: none;
  color: inherit;
  transition: background 0.15s;
}

.checklist-item:hover {
  background: #f5f6fa;
  text-decoration: none;
  color: inherit;
}

.checklist-item-check {
  font-size: 20px;
  flex-shrink: 0;
  color: #d0d5dd;
  line-height: 1;
}

.checklist-item.is-done .checklist-item-check {
  color: #34c38f;
}

.checklist-item-body {
  flex: 1;
  min-width: 0;
}

.checklist-item-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #1a1d26;
}

.checklist-item.is-done .checklist-item-label {
  color: #8b90a5;
  text-decoration: line-through;
}

.checklist-item-desc {
  display: block;
  font-size: 11px;
  color: #8b90a5;
  margin-top: 1px;
}

.checklist-item-arrow {
  color: #d0d5dd;
  font-size: 18px;
  flex-shrink: 0;
}

/* Tour button in checklist header */
.checklist-tour-btn {
  background: none;
  border: none;
  cursor: pointer;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: #556ee6;
  font-size: 18px;
  transition: all 0.2s;
}

.checklist-tour-btn:hover {
  background: #f0f2ff;
  color: #4458c9;
}

/* Locked items (dependencies not met) */
.checklist-item.is-locked {
  opacity: 0.55;
  cursor: default;
  pointer-events: none;
}

.checklist-item.is-locked .checklist-item-check {
  color: #d0d5dd;
}

.checklist-item-locked-hint {
  display: block;
  font-size: 10px;
  color: #c08a1e;
  margin-top: 2px;
  font-weight: 500;
}

/* CTA for pending (unlocked) items */
.checklist-item-cta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #556ee6;
  white-space: nowrap;
  flex-shrink: 0;
}

.checklist-item-cta i {
  font-size: 16px;
}

/* Pulse on first pending item */
@keyframes subtlePulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(85, 110, 230, 0); }
  50% { box-shadow: 0 0 0 4px rgba(85, 110, 230, 0.08); }
}

.checklist-item.is-next {
  animation: subtlePulse 2.5s ease-in-out infinite;
  background: rgba(85, 110, 230, 0.03);
}

/* Celebration state */
.checklist-celebration {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: rgba(52, 195, 143, 0.08);
  border-radius: 8px;
  margin-bottom: 8px;
}

.checklist-celebration-icon {
  font-size: 24px;
  color: #34c38f;
}

.checklist-celebration-text {
  font-size: 14px;
  font-weight: 600;
  color: #2ba57a;
}

/* === ASYMMETRIC GRID === */
.cards-grid--asymmetric {
  grid-template-columns: 1.4fr 1fr;
}

.cards-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* === RECENT PARTICIPANTS === */
.recent-participants-card {
  display: flex;
  flex-direction: column;
  min-height: 280px;
  max-height: none;
  overflow: visible;
}

.recent-participants-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.recent-participant {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 8px;
  border-radius: 6px;
  transition: background 0.15s;
}

.recent-participant:hover {
  background: #f8f9fc;
}

.rp-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}

.rp-info {
  flex: 1;
  min-width: 0;
}

.rp-name {
  font-size: 13px;
  font-weight: 600;
  color: #1a1d26;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rp-email {
  font-size: 11px;
  color: #8b90a5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rp-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 3px;
}

.rp-status {
  font-size: 10px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 5px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.rp-status--0 { background: rgba(139, 92, 246, 0.1); color: #8b5cf6; }
.rp-status--1 { background: rgba(85, 110, 230, 0.1); color: #556ee6; }
.rp-status--2 { background: rgba(241, 180, 76, 0.12); color: #c08a1e; }
.rp-status--3 { background: rgba(52, 195, 143, 0.1); color: #2ba57a; }
.rp-status--4 { background: rgba(244, 106, 106, 0.1); color: #f46a6a; }

.rp-time {
  font-size: 10px;
  color: #adb5bd;
}

.rp-view-all {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #556ee6;
  text-decoration: none;
  border-top: 1px solid #f1f3f5;
  margin-top: auto;
  transition: background 0.15s;
}

.rp-view-all:hover {
  background: #f0f2ff;
  text-decoration: none;
}

/* === INSIGHT TICKER === */
@keyframes ticker-scroll {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-25%); }
}

.insight-ticker {
  position: relative;
  overflow: hidden;
  margin-bottom: 20px;
}

/* fade edges */
.insight-ticker::before,
.insight-ticker::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 48px;
  z-index: 2;
  pointer-events: none;
}
.insight-ticker::before {
  left: 0;
  background: linear-gradient(to right, #f8f9fc 40%, transparent);
}
.insight-ticker::after {
  right: 0;
  background: linear-gradient(to left, #f8f9fc 40%, transparent);
}

.insight-ticker-track {
  display: flex;
  width: max-content;
  animation: ticker-scroll linear infinite;
}

.ticker-set {
  display: flex;
  gap: 8px;
  padding-right: 8px; /* gap entre os sets sem quebrar a costura */
}

.insight-ticker-track:hover {
  animation-play-state: paused;
}

.insight-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid transparent;
}

.insight-chip i { font-size: 14px; flex-shrink: 0; }

.insight-chip--positive {
  background: rgba(52, 195, 143, 0.08);
  border-color: rgba(52, 195, 143, 0.2);
  color: #1e9e6d;
}
.insight-chip--positive i { color: #34c38f; }

.insight-chip--warning {
  background: rgba(241, 180, 76, 0.1);
  border-color: rgba(241, 180, 76, 0.25);
  color: #9a6f00;
}
.insight-chip--warning i { color: #f1b44c; }

.insight-chip--danger {
  background: rgba(244, 106, 106, 0.08);
  border-color: rgba(244, 106, 106, 0.2);
  color: #c0392b;
}
.insight-chip--danger i { color: #f46a6a; }

.insight-chip--info {
  background: rgba(85, 110, 230, 0.07);
  border-color: rgba(85, 110, 230, 0.18);
  color: #3a4fa3;
}
.insight-chip--info i { color: #556ee6; }

/* === GOAL CARD — PROJECTION === */
.goal-status-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 5px;
}
.goal-status-badge--on_track { background: rgba(52,195,143,.12); color: #1e9e6d; }
.goal-status-badge--warning   { background: rgba(241,180,76,.12); color: #a07000; }
.goal-status-badge--risk      { background: rgba(244,106,106,.12); color: #c0392b; }

.goal-projection {
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 10px 4px;
  margin: 0 0 10px;
}

.goal-proj-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  flex: 1;
}

.goal-proj-divider {
  width: 1px;
  height: 26px;
  background: #e0e3ed;
  flex-shrink: 0;
}

.goal-proj-value {
  font-size: 12px;
  font-weight: 700;
  color: #1a1d26;
  line-height: 1.2;
}

.goal-proj-label {
  font-size: 10px;
  color: #8b90a5;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.goal-velocity-info {
  font-size: 12px;
  color: #f1b44c;
  cursor: help;
  margin-left: 2px;
  vertical-align: middle;
}
.goal-velocity-info:hover {
  color: #c08a1e;
}

.goal-alert {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin: 0 0 8px;
  padding: 8px 10px;
  border-radius: 7px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.4;
}

.goal-alert i { font-size: 15px; flex-shrink: 0; margin-top: 1px; }

.goal-alert--warning {
  background: rgba(241,180,76,.1);
  color: #9a6f00;
}
.goal-alert--warning i { color: #f1b44c; }

.goal-alert--risk {
  background: rgba(244,106,106,.08);
  color: #c0392b;
}
.goal-alert--risk i { color: #f46a6a; }

/* === GOAL CUSTOM MODE === */
.goal-header-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.goal-custom-badge {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 5px;
  background: rgba(99,102,241,.12);
  color: #6366f1;
}
.goal-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 5px;
  border: 1px solid #e3e6f0;
  background: #fff;
  color: #495057;
  cursor: pointer;
  line-height: 1.4;
  transition: background 0.15s, border-color 0.15s;
}
.goal-action-btn:hover { background: #f5f6fa; border-color: #c8cdd8; }
.goal-action-btn--danger { color: #c0392b; border-color: rgba(244,106,106,.4); }
.goal-action-btn--danger:hover { background: rgba(244,106,106,.06); }
.goal-action-btn--outline { color: #6366f1; border-color: rgba(99,102,241,.35); }
.goal-action-btn--outline:hover { background: rgba(99,102,241,.06); }
.goal-action-btn--icon { padding: 3px 7px; font-size: 14px; }
.goal-period-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: #6b6f82;
  margin-top: -12px;
  margin-bottom: 4px;
}
.goal-period-dates {
  display: flex;
  align-items: center;
  gap: 5px;
}
.goal-period-info i { font-size: 14px; color: #6366f1; }

/* === GOAL MODAL === */
.goal-modal-body { padding: 4px 0; }
.goal-modal-field { margin-bottom: 14px; }
.goal-modal-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.goal-modal-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 5px;
}
.goal-modal-error {
  font-size: 12px;
  color: #c0392b;
  background: rgba(244,106,106,.08);
  border-radius: 5px;
  padding: 6px 10px;
  margin-bottom: 12px;
}
.goal-modal-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-top: 4px;
}
.goal-modal-actions-right { display: flex; gap: 8px; }
.goal-modal-reset-btn {
  font-size: 12px;
  color: #c0392b;
  border: 1px solid rgba(244,106,106,.4);
  background: transparent;
  padding: 4px 10px;
  border-radius: 5px;
}
.goal-modal-reset-btn:hover { background: rgba(244,106,106,.07); }
.goal-metric-toggle {
  display: flex;
  background: #e9ecef;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
}
.goal-metric-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #6c757d;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
}
.goal-metric-btn i { font-size: 15px; }
.goal-metric-btn:hover:not(.active) {
  color: #495057;
  background: rgba(255,255,255,0.5);
}
.goal-metric-btn.active {
  background: #fff;
  color: #556ee6;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

/* === GOAL AUDIT LOG === */
.goal-log {
  margin-top: 20px;
  border-top: 1px solid #f1f3f5;
  padding-top: 14px;
}
.goal-log-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8b90a5;
  margin-bottom: 10px;
}
.goal-log-entry {
  padding: 8px 10px;
  border-radius: 6px;
  background: #f8f9fc;
  margin-bottom: 6px;
}
.goal-log-info {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 4px;
}
.goal-log-user {
  font-size: 12px;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 3px;
}
.goal-log-user i { font-size: 13px; color: #8b90a5; }
.goal-log-at {
  font-size: 11px;
  color: #8b90a5;
  margin-left: auto;
}
.goal-log-detail {
  font-size: 11px;
  color: #6b6f82;
  margin-top: 3px;
}
.goal-log-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 5px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.goal-log-badge--created { background: rgba(52,195,143,.12); color: #1e9e6d; }
.goal-log-badge--updated { background: rgba(85,110,230,.12); color: #3551c9; }
.goal-log-badge--removed { background: rgba(244,106,106,.12); color: #c0392b; }

/* === HEATMAP METRIC TOGGLE === */
.heatmap-metric-toggle {
  display: flex;
  background: #f5f6fa;
  border-radius: 5px;
  padding: 2px;
  gap: 2px;
  width: fit-content;
  margin-bottom: 10px;
}

.hmtab {
  background: none;
  border: none;
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 600;
  color: #8b90a5;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.hmtab:hover { color: #1a1d26; }

.hmtab.active {
  background: #fff;
  color: #556ee6;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

.hmtab--green.active { color: #34c38f; }

/* === HEATMAP INSIGHT === */
.heatmap-insight {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #556ee6;
  font-weight: 600;
  padding: 6px 4px 0;
}

.heatmap-insight i { font-size: 14px; color: #f1b44c; }

/* === RISK CARD === */
.risk-card { overflow: visible; min-height: auto; }

.risk-tier-bar {
  display: flex;
  height: 56px;
  border-radius: 10px;
  overflow: hidden;
  margin: 16px 0 12px;
  gap: 3px;
}

.risk-tier-seg {
  border-radius: 8px;
  transition: flex 0.4s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  min-width: 0;
}

.risk-tier-seg-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 4px;
}

.risk-seg-count {
  font-size: 16px;
  font-weight: 800;
  color: #fff;
  line-height: 1;
}

.risk-seg-label {
  font-size: 10px;
  font-weight: 600;
  color: rgba(255,255,255,0.85);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.risk-tier-seg--high   { background: #34c38f; }
.risk-tier-seg--medium { background: #f1b44c; }
.risk-tier-seg--low    { background: #f46a6a; }

.risk-tier-labels {
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
}

.risk-tier-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #4a5068;
}

.risk-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.risk-dot--high   { background: #34c38f; }
.risk-dot--medium { background: #f1b44c; }
.risk-dot--low    { background: #f46a6a; }

.risk-tier-count { font-weight: 700; color: #1a1d26; }
.risk-tier-name  { color: #8b90a5; }
.risk-tier-pct   { font-size: 11px; color: #b0b5c8; margin-left: -2px; }

.risk-list { border-top: 1px solid #f1f3f5; padding-top: 14px; }

.risk-list-title {
  font-size: 11px;
  font-weight: 700;
  color: #8b90a5;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 10px;
}

.risk-person {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #f8f9fb;
}
.risk-person:last-child { border-bottom: none; }

.risk-person-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #556ee6, #6366f1);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.risk-person-info {
  flex: 1;
  min-width: 0;
}

.risk-person-name {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #1a1d26;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.risk-person-reason {
  display: block;
  font-size: 11px;
  color: #8b90a5;
  margin-top: 1px;
}

.risk-tier-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 5px;
  flex-shrink: 0;
  white-space: nowrap;
}
.risk-tier-badge--low    { background: rgba(244,106,106,.1); color: #c0392b; }
.risk-tier-badge--medium { background: rgba(241,180,76,.1);  color: #9a6f00; }

.risk-all-good {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 0;
  color: #34c38f;
  font-size: 13px;
  font-weight: 500;
}
.risk-all-good i { font-size: 20px; }

/* === EMPTY STATES === */
.empty-state-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  text-align: center;
}

.empty-state-card .empty-icon {
  width: 48px;
  height: 48px;
  background: #f5f6fa;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  color: #b0b5c8;
  font-size: 22px;
}

.empty-title {
  font-size: 13px;
  font-weight: 600;
  color: #4a5068;
  margin-bottom: 4px;
}

.empty-desc {
  font-size: 12px;
  color: #8b90a5;
  line-height: 1.5;
  max-width: 220px;
}

.empty-state-page {
  text-align: center;
  padding: 80px 20px;
  color: #adb5bd;
}

.empty-state-page i {
  font-size: 64px;
  margin-bottom: 16px;
  display: block;
}

/* === RESPONSIVE === */
@media (max-width: 1100px) {
  .kpi-row { grid-template-columns: repeat(3, 1fr); }
  .cards-grid { grid-template-columns: 1fr; }
  .cards-grid--2 { grid-template-columns: 1fr; }
  .cards-grid--asymmetric { grid-template-columns: 1fr; }
  .cards-grid--funnel-timeline { grid-template-columns: 1fr; }
  .cards-grid--timeline-goal { grid-template-columns: 1fr; }
  .mini-kpi-row { grid-template-columns: repeat(2, 1fr); }
  .event-info-top { flex-direction: column; }
}

@media (max-width: 768px) {
  .kpi-row { grid-template-columns: repeat(2, 1fr); }
  .sa-header { flex-direction: column; }
  .sa-header-right { flex-wrap: wrap; }
  .event-info-actions { flex-wrap: wrap; }
  .event-info-meta { flex-direction: column; gap: 8px; }
  .checklist-header-left { flex-wrap: wrap; }
}
/* === KPI TREND === */
.kpi-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
}
.trend-up   { font-size: 11px; font-weight: 700; color: #34c38f; display: flex; align-items: center; gap: 2px; }
.trend-down { font-size: 11px; font-weight: 700; color: #f46a6a; display: flex; align-items: center; gap: 2px; }
.trend-neutral { font-size: 11px; font-weight: 600; color: #8b90a5; display: flex; align-items: center; gap: 2px; }
.kpi-trend-sub { font-size: 10px; color: #adb5bd; }
.kpi-value.warning { color: #f1b44c; }
.kpi-value.neutral { color: #8b90a5; }
.kpi-icon--warning { background: rgba(241, 180, 76, 0.1); color: #f1b44c; }
.kpi-icon--neutral { background: rgba(139, 144, 165, 0.1); color: #8b90a5; }

/* === CAPACITY BLOCK === */
.capacity-block {
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  padding: 20px 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(26,29,38,.04);
}
.capacity-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.capacity-title { font-size: 14px; font-weight: 700; color: #1a1d26; display: flex; align-items: center; }
.capacity-summary { font-size: 13px; color: #4a5068; font-weight: 500; display: flex; align-items: center; gap: 8px; }
.capacity-pct { font-weight: 700; }
.pct-success { color: #34c38f; }
.pct-warning { color: #f1b44c; }
.pct-danger  { color: #f46a6a; }
.capacity-bar-wrap { margin-bottom: 8px; }
.capacity-bar-track {
  height: 10px;
  background: #f1f3f5;
  border-radius: 99px;
  overflow: hidden;
  margin-bottom: 6px;
}
.capacity-bar-fill {
  height: 100%;
  border-radius: 99px;
  transition: width 0.6s ease;
}
.cap-bar--success { background: #34c38f; }
.cap-bar--warning { background: #f1b44c; }
.cap-bar--danger  { background: #f46a6a; }
.capacity-meta { font-size: 12px; color: #8b90a5; }
.capacity-remaining { color: #34c38f; font-weight: 600; }
.capacity-waiting   { color: #f1b44c; font-weight: 600; }
.capacity-full      { color: #f46a6a; font-weight: 700; }

/* === RC ITEMS TABLE === */
.rc-items-table-wrap { overflow-x: auto; margin-top: 14px; border-top: 1px solid #f1f3f5; padding-top: 14px; }

/* Mini KPI summary strip */
.rc-summary-kpis {
  display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px;
}
.rc-kpi {
  display: flex; flex-direction: column; align-items: center;
  background: #f8f9fb; border-radius: 8px; padding: 8px 14px; min-width: 80px; flex: 1;
}
.rc-kpi-label { font-size: 10px; font-weight: 700; color: #8b90a5; text-transform: uppercase; letter-spacing: .04em; margin-bottom: 4px; white-space: nowrap; }
.rc-kpi-value { font-size: 18px; font-weight: 800; color: #1a1d26; }
.rc-kpi--confirmed .rc-kpi-value { color: #34c38f; }
.rc-kpi--pending   .rc-kpi-value { color: #f1b44c; }
.rc-kpi--progress  .rc-kpi-value { color: #556ee6; }
.rc-kpi--undefined .rc-kpi-value { color: #8b5cf6; }
.rc-kpi--declined  .rc-kpi-value { color: #f46a6a; }

.rc-items-table { width: 100%; font-size: 13px; border-collapse: collapse; }
.rc-items-table th {
  font-size: 11px; font-weight: 700; color: #8b90a5; text-transform: uppercase;
  letter-spacing: .04em; padding: 0 10px 8px; text-align: left; border-bottom: 1px solid #f1f3f5; white-space: nowrap;
}
.rc-items-table th.text-center { text-align: center; }
.rc-col--confirmed { color: #34c38f !important; }
.rc-col--pending   { color: #f1b44c !important; }
.rc-col--progress  { color: #556ee6 !important; }
.rc-col--undefined { color: #8b5cf6 !important; }
.rc-col--declined  { color: #f46a6a !important; }

.rc-items-table td { padding: 8px 10px; border-bottom: 1px solid #f8f9fb; vertical-align: middle; }
.rc-items-table tbody tr:last-child td { border-bottom: none; }
.rc-items-table tbody tr:hover td { background: #fafbff; }
.rc-item-name { font-weight: 600; color: #1a1d26; }
.rc-item-identifier { display: inline-block; background: #f0f2ff; color: #556ee6; border-radius: 4px; padding: 1px 6px; font-size: 11px; font-weight: 700; margin-right: 6px; }
.rc-occ-wrap { display: flex; align-items: center; gap: 4px; justify-content: center; }
.rc-occ-bar { flex: 1; height: 6px; background: #f1f3f5; border-radius: 99px; overflow: hidden; min-width: 40px; max-width: 60px; }
.rc-occ-fill { height: 100%; border-radius: 99px; transition: width .5s ease; }
.rc-bar--success { background: #34c38f; }
.rc-bar--warning { background: #f1b44c; }
.rc-bar--danger  { background: #f46a6a; }
.rc-occ-pct { font-size: 11px; font-weight: 700; white-space: nowrap; }
.rc-remaining { color: #34c38f; font-weight: 700; font-size: 12px; }
.rc-waiting   { color: #f1b44c; font-weight: 600; font-size: 12px; }
.rc-full      { color: #f46a6a; font-weight: 700; font-size: 12px; }

/* Pending breakdown expandable */
.rc-col--expand { width: 32px; text-align: center; }
.rc-expand-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid #e3e6f0;
  background: #fff;
  color: #adb5bd;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.rc-expand-btn:hover,
.rc-expand-btn.active { background: rgba(241,180,76,.1); border-color: rgba(241,180,76,.4); color: #f1b44c; }
.rc-breakdown-row td {
  padding: 3px 10px;
  background: #fdfaf3;
  border-bottom: none;
}
.rc-breakdown-row:last-of-type td { border-bottom: 1px solid #f1e8c8; }
.rc-breakdown-name {
  color: #6b6f82;
  padding-left: 20px !important;
  text-transform: uppercase;
}
.rc-breakdown-row--none .rc-breakdown-name { color: #adb5bd; font-style: italic; }
.rc-breakdown-count { font-weight: 600; }

/* Sub-linha de acompanhantes (recorte do total do grupo) — tom índigo p/ distinguir das pendências */
.rc-breakdown-row--companions td { background: #f4f3fb; border-bottom: none; }
.rc-breakdown-row--companions .rc-breakdown-name { color: #5e54d6; }
.rc-breakdown-row--companions .rc-breakdown-name .bx { font-size: 13px; vertical-align: middle; }

/* Footer totals row */
.rc-tfoot-row td {
  padding: 10px 10px; border-top: 2px solid #e8eaed;
  background: #f4f6fb; font-size: 13px;
}
.rc-tfoot-label { font-weight: 700; color: #556ee6; font-size: 12px; text-transform: uppercase; letter-spacing: .04em; }
.rc-tfoot-companions td { padding: 7px 10px; background: #f4f3fb; border-top: 1px solid #e4e2f3; font-size: 12px; }
.rc-tfoot-companions-label { font-weight: 700; color: #5e54d6; text-transform: uppercase; letter-spacing: .04em; }
.rc-tfoot-companions-label .bx { font-size: 13px; vertical-align: middle; }

/* === GROUP RANKING === */
.group-ranking { display: flex; flex-direction: column; gap: 14px; }
.group-rank-row { display: flex; align-items: flex-start; gap: 12px; }
.group-rank-pos {
  width: 24px; height: 24px; border-radius: 50%;
  background: #f1f3f5; color: #8b90a5;
  font-size: 11px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; margin-top: 2px;
}
.rank-gold   { background: #fff3d4; color: #b7791f; }
.rank-silver { background: #f0f0f0; color: #6b7280; }
.rank-bronze { background: #fce8da; color: #9c4221; }
.group-rank-info { flex: 1; min-width: 0; }
.group-rank-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
.group-rank-name { font-size: 13px; font-weight: 600; color: #1a1d26; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 160px; }
.group-rank-rate { font-size: 14px; font-weight: 800; }
.group-rank-bar-track { height: 6px; background: #f1f3f5; border-radius: 99px; overflow: hidden; margin-bottom: 4px; }
.group-rank-bar-fill { height: 100%; border-radius: 99px; transition: width .5s ease; }
.fill-success { background: #34c38f; }
.fill-warning { background: #f1b44c; }
.fill-danger  { background: #f46a6a; }
.mg-rate-inline { display: flex; align-items: center; gap: 6px; justify-content: center; }
.mg-rate-bar { flex: 1; background: #f1f3f5; border-radius: 99px; overflow: hidden; min-width: 40px; max-width: 60px; height: 8px; }
.mg-rate-bar--sm { height: 6px; }
.mg-rate-bar-fill { height: 100%; border-radius: 99px; transition: width .5s ease; }
.group-rank-meta { display: flex; justify-content: space-between; font-size: 11px; color: #8b90a5; }

/* === CARDS GRID 3 cols === */
.cards-grid--3 { grid-template-columns: repeat(3, 1fr); }

/* === DEMOGRAPHICS === */
.geo-tabs { display: flex; gap: 4px; margin-bottom: 14px; }
.geo-tab {
  background: none; border: 1px solid #eaecf2; border-radius: 6px;
  padding: 3px 12px; font-size: 12px; font-weight: 600; color: #8b90a5; cursor: pointer;
}
.geo-tab.active { background: #556ee6; color: #fff; border-color: #556ee6; }
.geo-list { display: flex; flex-direction: column; gap: 8px; }
.geo-list-row { display: flex; align-items: center; gap: 8px; }
.geo-rank { width: 18px; font-size: 11px; font-weight: 700; color: #8b90a5; flex-shrink: 0; }
.geo-name { font-size: 12px; font-weight: 600; color: #1a1d26; width: 90px; flex-shrink: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.geo-state { font-weight: 400; color: #8b90a5; }
.geo-bar-track { flex: 1; height: 6px; background: #f1f3f5; border-radius: 99px; overflow: hidden; }
.geo-bar-fill { height: 100%; background: #556ee6; border-radius: 99px; }
.geo-total { font-size: 12px; font-weight: 700; color: #4a5068; width: 32px; text-align: right; flex-shrink: 0; }
</style>
